import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { format, parseISO, addDays, subDays, addWeeks, subWeeks, addMonths, subMonths, addQuarters, subQuarters, addYears, subYears, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear, endOfYear, eachDayOfInterval } from 'date-fns';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Plus, Loader2, CalendarIcon, Search, X, Eye, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { useScheduler } from '@/contexts/SchedulerContext';
import { useScreenData } from '@/hooks/useScreenData';
import { useFilters } from '@/contexts/FilterContext';
import { useTimezone } from '@/hooks/use-timezone';
import { CalendarView, Resource, Booking, Absence, BankHoliday, BookingStatus } from '@/types';
import { BryntumSchedulerTimeline, BryntumSchedulerTimelineHandle, TimelineGroupingMode } from './BryntumSchedulerTimeline';
import { ComprehensiveBookingModal } from './ComprehensiveBookingModal';
import { CalendarFilterDrawer, CalendarFilterDrawerTrigger } from './CalendarFilterDrawer';
import { BookingDragConfirmDialog } from './BookingDragConfirmDialog';
import { DeleteBookingConfirmDialog } from './DeleteBookingConfirmDialog';
import { EditBookingConfirmDialog } from './EditBookingConfirmDialog';
import { CurrentTimezoneSelector } from './CurrentTimezoneSelector';
import { QuickAddBookingModal, QuickAddBookingData } from './QuickAddBookingModal';
import { MoveBookingDialog } from '@/components/booking/MoveBookingDialog';
import { AIBatchSuggestModal } from '@/components/unallocated/AIBatchSuggestModal';
import { DataModeToggle } from '@/components/DataModeToggle';
import { FeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { useCompany } from '@/contexts/CompanyContext';
import { useUser } from '@/contexts/UserContext';
import { useBookingDrag } from '@/hooks/use-booking-drag';
import { useDateRangeBookings } from '@/hooks/use-date-range-bookings';
import { convertBookingTimezone } from '@/utils/timezone-utils';
import { toast as sonnerToast } from 'sonner';
import { mockAbsences, mockBankHolidays } from '@/lib/mock-data';
import { HeatmapLegend } from './HeatmapLegend';
import { BurnoutRiskPanelWrapper } from './BurnoutRiskPanel';
import { WhatIfScenario } from '@/types';
import { HelpButton } from '@/components/help/HelpButton';
import { useConflictResolutionSettings } from '@/hooks/useConflictResolutionSettings';
import { bookingTypeUsagesApi, resourcesApi, absencesApi, bookingsApi, type BookingTypeUsageDto } from '@/services/api';
import { type PlanPreviewScenario } from '@/types/resourceRequest';

// Helper function to format a Date as ISO-like string using LOCAL time values
// NO "Z" suffix - this prevents JavaScript from interpreting as UTC and applying timezone offset
const formatDateAsLocalISO = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const ms = String(date.getMilliseconds()).padStart(3, '0');
  // No Z suffix to prevent UTC interpretation
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${ms}`;
};

interface ResourceCalendarProps {
  initialHeatmapMode?: boolean;
  /** Pre-selects a project filter when the heatmap is opened from Project Staffing */
  initialProjectFilter?: string | null;
  /** What-if scenario handed off from the heatmap panel — activates what-if mode */
  whatIfScenario?: WhatIfScenario | null;
  /** Called when the scenario is committed (true) or discarded (false) */
  onWhatIfComplete?: (navigateBackToHeatmap?: boolean) => void;
  /** Heatmap-mode only: called when the burnout panel wants to navigate to calendar */
  onNavigateToCalendarWithWhatIf?: (scenario: WhatIfScenario) => void;
  /** Plan preview scenario from Open Demand — shows ghost preview bookings on the timeline */
  planPreviewScenario?: PlanPreviewScenario | null;
  /** Called when the user dismisses the plan preview banner */
  onPlanPreviewClose?: () => void;
}

export function ResourceCalendar({
  initialHeatmapMode = false,
  initialProjectFilter,
  whatIfScenario,
  onWhatIfComplete,
  onNavigateToCalendarWithWhatIf,
  planPreviewScenario,
  onPlanPreviewClose,
}: ResourceCalendarProps) {
  const { calendarView, setCalendarView, selectedDate, setSelectedDate, isLoading: isContextLoading, isGroupsLoading, refreshResourcesAndGroups, refreshProjects, demoBookingTypeUsages } = useScheduler();
  const { resources, bookings: allBookings, projectDetails, costCentres, schedulerGroups, settings, addBooking, createBatchBookings, updateBooking, deleteBooking, currentMode } = useScreenData('calendar');
  const { applyFilters, setFilterStats, hasActiveFilters, filters, hasActiveAdvancedFilters, advancedFilters, showFilteredBookings, updateFilter } = useFilters();
  const { hasCompanyAccess, isLoading: isCompanyLoading, currentCompany } = useCompany();
  const { user } = useUser();
  const [isInitialized, setIsInitialized] = useState(false);

  // Track whether the first context data load has completed so the in-calendar
  // overlay is suppressed while the full-page TimelineLoadingOverlay is active.
  const hasCompletedFirstLoad = useRef(false);
  if (!isContextLoading && !isCompanyLoading && currentMode !== 'demo') {
    hasCompletedFirstLoad.current = true;
  }

  // ── Booking Type Usage permissions (for restricting timeline interactions) ──
  const [bookingTypeUsages, setBookingTypeUsages] = useState<BookingTypeUsageDto[] | null>(null);
  useEffect(() => {
    if (currentMode === 'demo') {
      setBookingTypeUsages(demoBookingTypeUsages);
      return;
    }
    let cancelled = false;
    resourcesApi.me()
      .then(resource => {
        if (cancelled) return [];
        return bookingTypeUsagesApi.getByResourceId(Number(resource.id));
      })
      .then(usages => { if (!cancelled) setBookingTypeUsages(usages); })
      .catch(() => { if (!cancelled) setBookingTypeUsages([]); });
    return () => { cancelled = true; };
  }, [currentMode, demoBookingTypeUsages]);

  // Pre-apply project filter when navigating here from Project Staffing
  useEffect(() => {
    if (initialProjectFilter) {
      updateFilter('projects', [initialProjectFilter]);
    }
    // Only run once on mount — intentionally omitting updateFilter from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Local state for pending recurring bookings (optimistic UI, no API calls)
  // Declared early because it's used in the bookings useMemo below
  const [pendingRecurringBookings, setPendingRecurringBookings] = useState<Booking[]>([]);

  // Loading overlay state for timeline during add/update booking
  const [isSavingBooking, setIsSavingBooking] = useState(false);

  // Re-fetch resources, scheduler groups, and projects every time the Planner
  // tab is activated (i.e. this component mounts).  This ensures that company
  // access changes and the latest project custom field values are reflected
  // immediately when the user navigates back to the Planner.
  useEffect(() => {
    refreshResourcesAndGroups();
    refreshProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run only on mount
  }, []);

  const {
    bookings: dateRangeBookings,
    refetchLiveBookings,
    removeBookingFromCache,
    isLoading: isLoadingBookings,
    dateRange,
  } = useDateRangeBookings({
    calendarView,
    selectedDate,
    allBookings,
    mode: currentMode,
  });

  // Merge real bookings with pending recurring bookings for display
  const bookings = useMemo(() => [
    ...dateRangeBookings,
    ...pendingRecurringBookings,
  ], [dateRangeBookings, pendingRecurringBookings]);

  // ── Absences: fetch from backend when in live mode, fall back to mock ──
  const [absences, setAbsences] = useState<Absence[]>(mockAbsences);
  useEffect(() => {
    if (currentMode === 'demo') {
      setAbsences(mockAbsences);
      return;
    }
    let cancelled = false;
    const startIso = dateRange.startDate.toISOString();
    const endIso = dateRange.endDate.toISOString();
    absencesApi.getByDateRange(startIso, endIso, currentCompany?.id)
      .then(data => { if (!cancelled) setAbsences(data); })
      .catch(() => { if (!cancelled) setAbsences([]); });
    return () => { cancelled = true; };
  }, [currentMode, dateRange.startDate, dateRange.endDate, currentCompany?.id]);

  const [zoomLevel, setZoomLevel] = useState(() => calendarView === 'day' ? 1 : 0); // Day view defaults to 1 (15-min), all others default to 0
  const [isFilterTrayOpen, setIsFilterTrayOpen] = useState(false);
  const [bookingSearchQuery, setBookingSearchQuery] = useState('');
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [bookingModalMode, setBookingModalMode] = useState<'create' | 'edit'>('create');
  const [editingBooking, setEditingBooking] = useState<Booking | undefined>(undefined);
  const [preselectedResource, setPreselectedResource] = useState<Resource | undefined>(undefined);
    const [preselectedDate, setPreselectedDate] = useState<Date | undefined>(undefined);
    const [preselectedEndDate, setPreselectedEndDate] = useState<Date | undefined>(undefined);
    const [preselectedStartTime, setPreselectedStartTime] = useState<{ hour: number; minute: number } | undefined>(undefined);
    const [preselectedEndTime, setPreselectedEndTime] = useState<{ hour: number; minute: number } | undefined>(undefined);
  const [preselectedBookingData, setPreselectedBookingData] = useState<Partial<Booking> | undefined>(undefined);
  
  // Quick Add Booking modal state
  const [isQuickAddModalOpen, setIsQuickAddModalOpen] = useState(false);
  const [quickAddPreselectedDate, setQuickAddPreselectedDate] = useState<Date | undefined>(undefined);
  const [quickAddPreselectedResource, setQuickAddPreselectedResource] = useState<Resource | undefined>(undefined);
  const [quickAddDefaultStartTime, setQuickAddDefaultStartTime] = useState<string>('09:00');
  const [quickAddDefaultEndTime, setQuickAddDefaultEndTime] = useState<string>('17:00');
  
  // Use cascading timezone system (user override or system default)
  const { timezone: effectiveTimezone, setUserTimezone } = useTimezone();
  
  // Update timezone when effective timezone changes (from user override or system default)
  // Copied booking state for paste functionality
  const [copiedBooking, setCopiedBooking] = useState<Booking | null>(null);
  
  // Delete confirmation dialog state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState<Booking | null>(null);
  
  // Edit confirmation dialog state for recurring bookings
  const [showEditConfirm, setShowEditConfirm] = useState(false);
  const [bookingToEdit, setBookingToEdit] = useState<Booking | null>(null);
  // Tracks whether the user chose "this booking" or "all future bookings" in the edit dialog
  const [editScope, setEditScope] = useState<'this' | 'all-future' | null>(null);
  // Tracks the specific resource row that was clicked when opening the edit modal
  const [editContextResourceId, setEditContextResourceId] = useState<string | undefined>(undefined);

  // Move booking dialog state
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [bookingToMove, setBookingToMove] = useState<Booking | null>(null);

  // Resolve conflict — batch assignment dialog state
  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [bookingsToResolve, setBookingsToResolve] = useState<Booking[]>([]);

  
  // Timezone state - initialize based on whether timezones are enabled
  const [selectedTimezone, setSelectedTimezone] = useState<string>(
    settings.enableTimezones ? (settings.defaultTimezone || 'Europe/London') : 'Europe/London'
  );
  
  // Update timezone when settings or effective timezone changes
  // When timezone feature is disabled, always use Europe/London so no offset is applied
  // When enabled, use the effective timezone (user override or system default)
  useEffect(() => {
    if (!settings.enableTimezones) {
      setSelectedTimezone('Europe/London');
      return;
    }
    const tz = effectiveTimezone || settings.defaultTimezone || 'Europe/London';
    setSelectedTimezone(tz);
  }, [effectiveTimezone, settings.defaultTimezone, settings.enableTimezones]);
  
  // Update quick add default times from business hours settings
  useEffect(() => {
    const formatTime = (hour: number) => {
      return `${String(hour).padStart(2, '0')}:00`;
    };
    
    if (settings.businessHoursStart !== undefined) {
      setQuickAddDefaultStartTime(formatTime(settings.businessHoursStart));
    }
    if (settings.businessHoursEnd !== undefined) {
      setQuickAddDefaultEndTime(formatTime(settings.businessHoursEnd));
    }
  }, [settings.businessHoursStart, settings.businessHoursEnd]);
  
  
  // Date picker popover state
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  
  // Normalize legacy timeline view values to current TimelineGroupingMode values
  const normalizeGroupingMode = (value: string | undefined): TimelineGroupingMode => {
    if (!value) return 'resource';
    const normalized = value.toLowerCase();
    if (normalized === 'resource') return 'resource';
    if (normalized === 'project & resource' || normalized === 'project-resource') return 'project-resource';
    if (normalized === 'role & resource' || normalized === 'role-resource') return 'role-resource';
    if (normalized === 'customer') return 'customer';
    return 'resource';
  };

  // Timeline grouping mode state - initialize from saved settings default
  const [groupingMode, setGroupingMode] = useState<TimelineGroupingMode>(
    normalizeGroupingMode(settings.timelineDefaultView)
  );
  
  // Sync grouping mode when settings.timelineDefaultView changes
  useEffect(() => {
    if (settings.timelineDefaultView) {
      setGroupingMode(normalizeGroupingMode(settings.timelineDefaultView));
    }
  }, [settings.timelineDefaultView]);
  
  // Track whether user has manually changed the grouping to skip initial render
  const [hasUserChangedGrouping, setHasUserChangedGrouping] = useState(false);

  // Dynamically measure the Bryntum column header height so the grouping Select
  // can be perfectly vertically centred regardless of theme or time-axis row count.
  const [groupingSelectTop, setGroupingSelectTop] = useState<number>(14);
  useEffect(() => {
    const measure = () => {
      // The left-column header cell spans the full height of the time axis header
      const headerCell = document.querySelector('.b-grid-header-container .b-grid-cell');
      if (headerCell) {
        const h = (headerCell as HTMLElement).offsetHeight;
        if (h > 0) setGroupingSelectTop(Math.round((h - 32) / 2) + 65);
      }
    };
    // Try immediately, then retry as Bryntum renders asynchronously
    measure();
    const raf1 = requestAnimationFrame(measure);
    const raf2 = requestAnimationFrame(() => requestAnimationFrame(measure));
    const timer = setTimeout(measure, 500);
    const timer2 = setTimeout(measure, 1500);
    const observer = new MutationObserver(measure);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      clearTimeout(timer);
      clearTimeout(timer2);
      observer.disconnect();
    };
  }, []);

  // Refetch bookings when grouping mode changes so the new view has fresh data
  useEffect(() => {
    if (hasUserChangedGrouping && currentMode === 'live') {
      refetchLiveBookings();
    }
  }, [groupingMode, hasUserChangedGrouping, currentMode, refetchLiveBookings]);

  // Heatmap mode state
  const [isHeatmapMode, setIsHeatmapMode] = useState(initialHeatmapMode);


  // ── What-if mode ─────────────────────────────────────────────────────────
  // When a scenario arrives from the heatmap panel, navigate the calendar to
  // the target week and switch to week view so both booking rows are visible.
  useEffect(() => {
    if (whatIfScenario) {
      setSelectedDate(whatIfScenario.targetWeekStart);
      setCalendarView('week');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whatIfScenario]);

  const handleWhatIfCommit = useCallback(async () => {
    if (!whatIfScenario) return;

    if (whatIfScenario.type === 'rebalance') {
      // ── Persist the move: reassign every at-risk booking to the candidate ──
      const bookingIds = whatIfScenario.atRiskBookingIds ?? [];
      const targetResourceId = whatIfScenario.candidateResourceId;
      if (bookingIds.length > 0 && targetResourceId) {
        await Promise.all(
          bookingIds.map((id) =>
            updateBooking(id, { resourceId: String(targetResourceId) })
          )
        );
      }

      const project = whatIfScenario.projectName ?? 'Project';
      const candidate = whatIfScenario.candidateResourceName ?? 'resource';
      const weekLabel = whatIfScenario.affectedWeekStarts?.[0]
        ? `W${Math.ceil((whatIfScenario.affectedWeekStarts[0].getTime() - new Date(whatIfScenario.affectedWeekStarts[0].getFullYear(), 0, 1).getTime()) / 604800000)}`
        : '';
      sonnerToast.success(`${project} moved to ${candidate}${weekLabel ? ` · ${weekLabel}` : ''} confirmed`, {
        duration: 4000,
      });
    } else {
      const name = whatIfScenario.freeResourceName ?? 'Resource';
      sonnerToast.success(`Booking assigned to ${name} · W${whatIfScenario.freeFromWeek ?? ''} confirmed`, {
        duration: 4000,
      });
    }

    onWhatIfComplete?.(true); // true = navigate back to heatmap
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whatIfScenario, onWhatIfComplete, updateBooking]);

  const handleWhatIfDiscard = useCallback(() => {
    onWhatIfComplete?.(true); // discard → also navigate back to heatmap
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onWhatIfComplete]);

  // Ref for Bryntum scheduler to revert events on discard
  const schedulerTimelineRef = useRef<BryntumSchedulerTimelineHandle>(null);

  // Ref to the outermost scheduler DOM element — used by BurnoutRiskPanel for DOM injection
  const schedulerDomRef = useRef<HTMLDivElement | null>(null);
  
  // Capacity risk feature flag
  const { whatIfEnabled } = useConflictResolutionSettings();

  // Drag and drop state
  const { cancelDrag } = useBookingDrag();
  const [showDragConfirm, setShowDragConfirm] = useState(false);
  const [pendingDragUpdate, setPendingDragUpdate] = useState<{
    booking: Booking;
    originalResource: Resource;
    newResource: Resource;
    originalStartTime: Date;
    originalEndTime: Date;
    newStartTime: Date;
    newEndTime: Date;
    isResize?: boolean;
  } | null>(null);

  // Apply filters to resources and bookings
  const filteredData = useMemo(() => {
    const result = applyFilters(resources, bookings, settings, projectDetails, costCentres, dateRange);
    
    // Apply booking search query filter
    let searchFilteredBookings = result.filteredBookings;
    if (bookingSearchQuery.trim()) {
      const query = bookingSearchQuery.toLowerCase();
      
      searchFilteredBookings = result.filteredBookings.filter(booking => {
        // Get resource details
        const resource = resources.find(r => String(r.id) === String(booking.resourceId));
        const resourceName = resource?.name?.toLowerCase() || '';
        const resourceGroup = resource?.schedulerGroup?.toLowerCase() || '';
        const resourceDepartment = resource?.department?.toLowerCase() || '';
        
        // Get project details
        const project = projectDetails.find(p => String(p.id) === String(booking.projectId));
        const projectCode = project?.billingCode?.toLowerCase() || '';
        const projectName = booking.projectName?.toLowerCase() || '';
        
        // Get cost centre details
        const costCentre = costCentres.find(cc => String(cc.id) === String(booking.costCentreId));
        const costCentreCode = costCentre?.code?.toLowerCase() || '';
        const costCentreName = costCentre?.name?.toLowerCase() || '';
        
        // Get description
        const description = booking.description?.toLowerCase() || '';
        
        // Check if any field matches the search query
        return (
          resourceName.includes(query) ||
          resourceGroup.includes(query) ||
          resourceDepartment.includes(query) ||
          projectCode.includes(query) ||
          projectName.includes(query) ||
          costCentreCode.includes(query) ||
          costCentreName.includes(query) ||
          description.includes(query)
        );
      });
    }
    
    // If showFilteredBookings is true, include all bookings but mark filtered ones
    if (showFilteredBookings) {
      const filteredBookingIds = new Set(searchFilteredBookings.map(b => b.id));
      
      // Add isFiltered flag to all bookings
      const allBookingsWithFlag = bookings.map(booking => ({
        ...booking,
        isFiltered: !filteredBookingIds.has(booking.id)
      }));
      
      return {
        ...result,
        filteredBookings: allBookingsWithFlag
      };
    }
    
    return {
      ...result,
      filteredBookings: searchFilteredBookings
    };
  }, [resources, bookings, applyFilters, settings, projectDetails, costCentres, showFilteredBookings, currentMode, bookingSearchQuery, dateRange]);

  // Update filter stats in a separate effect to avoid setState during render
  useEffect(() => {
    setFilterStats(filteredData.stats);
  }, [filteredData.stats, setFilterStats]);

  // ── Resource Time Ranges ──────────────────────────────────────────────
  // Compute resourceTimeRanges here (absences, bank holidays, non-working
  // weekdays) and pass them down to BryntumSchedulerTimeline as a prop so
  // the timeline component stays presentation-only.
  // Weekends are intentionally excluded — no shading is applied for them.
  const resourceTimeRanges = useMemo(() => {
    const ranges: Array<{
      id: string;
      resourceId: string;
      name: string;
      startDate: Date;
      endDate: Date;
      timeRangeColor?: string;
      cls?: string;
      style?: string;
    }> = [];

    const filteredResources = filteredData.filteredResources;
    const filteredBookings = filteredData.filteredBookings;

    // Wait until resources have been loaded into state before computing
    // ranges.  This prevents premature empty ranges that never get
    // repopulated because the Bryntum store refresh already fired.
    if (filteredResources.length === 0) {
      return ranges;
    }

    const bankHolidays: BankHoliday[] = mockBankHolidays;

    // Get colors from settings
    const absenceColor = settings.absenceColor || '#94a3b8';
    const publicHolidayColor = settings.publicHolidayColor || '#fbbf24';
    const businessHoursStart = settings.businessHoursStart || 9;
    const businessHoursEnd = settings.businessHoursEnd || 17;

    // Process absences (only if showAbsence is enabled)
    if (settings.showAbsence !== false) {
      absences.forEach((absence) => {
        // Skip annual-leave absences — they are rendered as dashed event bars instead
        const reasonNorm = absence.reason.toLowerCase().replace(/[-_]/g, ' ');
        if (reasonNorm.includes('annual leave')) return;
        const absenceStartDate = new Date(absence.startDate);
        const absenceEndDate = new Date(absence.endDate);

        const currentDate = new Date(absenceStartDate);
        while (currentDate <= absenceEndDate) {
          let rangeStart: Date;
          let rangeEnd: Date;

          switch (absence.type) {
            case 'full-day':
              rangeStart = new Date(currentDate);
              rangeStart.setHours(0, 0, 0, 0);
              rangeEnd = new Date(currentDate);
              rangeEnd.setHours(23, 59, 59, 999);
              break;
            case 'half-day-am':
              rangeStart = new Date(currentDate);
              rangeStart.setHours(businessHoursStart, 0, 0, 0);
              rangeEnd = new Date(currentDate);
              rangeEnd.setHours(12, 0, 0, 0);
              break;
            case 'half-day-pm':
              rangeStart = new Date(currentDate);
              rangeStart.setHours(12, 0, 0, 0);
              rangeEnd = new Date(currentDate);
              rangeEnd.setHours(businessHoursEnd, 0, 0, 0);
              break;
            case 'hourly':
              if (absence.startTime && absence.endTime) {
                const [startHour, startMin] = absence.startTime.split(':').map(Number);
                const [endHour, endMin] = absence.endTime.split(':').map(Number);
                rangeStart = new Date(currentDate);
                rangeStart.setHours(startHour, startMin, 0, 0);
                rangeEnd = new Date(currentDate);
                rangeEnd.setHours(endHour, endMin, 0, 0);
              } else {
                rangeStart = new Date(currentDate);
                rangeStart.setHours(0, 0, 0, 0);
                rangeEnd = new Date(currentDate);
                rangeEnd.setHours(23, 59, 59, 999);
              }
              break;
            default:
              rangeStart = new Date(currentDate);
              rangeStart.setHours(0, 0, 0, 0);
              rangeEnd = new Date(currentDate);
              rangeEnd.setHours(23, 59, 59, 999);
          }

          if (groupingMode === 'project-resource') {
            const projectsForResource = new Set<string>();
            filteredBookings.forEach(booking => {
              if (String(booking.resourceId) === String(absence.resourceId) && booking.projectId) {
                projectsForResource.add(booking.projectId);
              }
            });
            projectsForResource.forEach(projectId => {
              ranges.push({
                id: `absence-${absence.id}-${projectId}-${currentDate.toISOString().split('T')[0]}`,
                resourceId: `${projectId}-${absence.resourceId}`,
                name: absence.reason || 'Absence',
                startDate: rangeStart,
                endDate: rangeEnd,
                timeRangeColor: absenceColor,
                cls: 'absence-range',
              });
            });
          } else if (groupingMode === 'role-resource') {
            const resource = filteredResources.find(r => String(r.id) === String(absence.resourceId));
            const roleName = resource?.role || 'Unassigned';
            ranges.push({
              id: `absence-${absence.id}-${roleName}-${currentDate.toISOString().split('T')[0]}`,
              resourceId: `${roleName}-${absence.resourceId}`,
              name: absence.reason || 'Absence',
              startDate: rangeStart,
              endDate: rangeEnd,
              timeRangeColor: absenceColor,
              cls: 'absence-range',
            });
          } else {
            ranges.push({
              id: `absence-${absence.id}-${absence.resourceId}-${currentDate.toISOString().split('T')[0]}`,
              resourceId: absence.resourceId,
              name: absence.reason || 'Absence',
              startDate: rangeStart,
              endDate: rangeEnd,
              timeRangeColor: absenceColor,
              cls: 'absence-range',
            });
          }

          currentDate.setDate(currentDate.getDate() + 1);
        }
      });
    }

    // Process bank holidays (only if showPublicHolidays is enabled)
    if (settings.showPublicHolidays !== false) {
      bankHolidays.forEach((holiday) => {
        const holidayDate = new Date(holiday.date);
        const rangeStart = new Date(holidayDate);
        rangeStart.setHours(0, 0, 0, 0);
        const rangeEnd = new Date(holidayDate);
        rangeEnd.setHours(23, 59, 59, 999);

        if (groupingMode === 'project-resource') {
          const projectResourceCombinations = new Set<string>();
          filteredBookings.forEach(booking => {
            if (booking.projectId && booking.resourceId) {
              projectResourceCombinations.add(`${booking.projectId}-${booking.resourceId}`);
            }
          });
          projectResourceCombinations.forEach(combinedId => {
            ranges.push({
              id: `holiday-${holiday.id}-${combinedId}`,
              resourceId: combinedId,
              name: holiday.name,
              startDate: rangeStart,
              endDate: rangeEnd,
              timeRangeColor: publicHolidayColor,
              cls: 'bank-holiday-range',
            });
          });
        } else if (groupingMode === 'role-resource') {
          filteredResources.forEach((resource) => {
            const roleName = resource.role || 'Unassigned';
            ranges.push({
              id: `holiday-${holiday.id}-${roleName}-${resource.id}`,
              resourceId: `${roleName}-${resource.id}`,
              name: holiday.name,
              startDate: rangeStart,
              endDate: rangeEnd,
              timeRangeColor: publicHolidayColor,
              cls: 'bank-holiday-range',
            });
          });
        } else {
          filteredResources.forEach((resource) => {
            ranges.push({
              id: `holiday-${holiday.id}-${resource.id}`,
              resourceId: resource.id,
              name: holiday.name,
              startDate: rangeStart,
              endDate: rangeEnd,
              timeRangeColor: publicHolidayColor,
              cls: 'bank-holiday-range',
            });
          });
        }
      });
    }

    // ── Non-working weekdays ───────────────────────────────────────────

    // Determine visible date range
    let visibleStart: Date;
    let visibleEnd: Date;
    const validDate = selectedDate instanceof Date && !isNaN(selectedDate.getTime())
      ? selectedDate
      : new Date();

    switch (calendarView) {
      case 'day':
        visibleStart = new Date(validDate);
        visibleStart.setHours(0, 0, 0, 0);
        visibleEnd = new Date(validDate);
        visibleEnd.setHours(23, 59, 59, 999);
        break;
      case 'week':
        visibleStart = startOfWeek(new Date(validDate), { weekStartsOn: 1 });
        visibleEnd = endOfWeek(new Date(validDate), { weekStartsOn: 1 });
        break;
      case 'month':
        visibleStart = startOfMonth(validDate);
        visibleEnd = endOfMonth(validDate);
        break;
      case 'quarter':
        visibleStart = startOfQuarter(validDate);
        visibleEnd = endOfQuarter(validDate);
        break;
      case 'year':
        visibleStart = startOfYear(validDate);
        visibleEnd = endOfYear(validDate);
        break;
      default:
        visibleStart = startOfWeek(new Date(validDate), { weekStartsOn: 1 });
        visibleEnd = endOfWeek(new Date(validDate), { weekStartsOn: 1 });
    }

    const isVisibleStartValid = visibleStart instanceof Date && !isNaN(visibleStart.getTime());
    const isVisibleEndValid = visibleEnd instanceof Date && !isNaN(visibleEnd.getTime());
    if (!isVisibleStartValid || !isVisibleEndValid || visibleStart.getTime() >= visibleEnd.getTime()) {
      return ranges;
    }

    const visibleDays = eachDayOfInterval({ start: visibleStart, end: visibleEnd });
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
    // Non-working weekdays: days where resource has 0 working hours.
    // Only weekdays (Mon-Fri) are checked — weekends are intentionally
    // excluded so they do not get any shading at all.
    filteredResources.forEach((resource) => {
      visibleDays.forEach((day) => {
        const dow = day.getDay();
        // Skip weekends — we only shade non-working *weekdays*
        if (dow === 0 || dow === 6) return;

        const dayKey = dayNames[dow];

        let workingHours: number;
        if (resource.useCustomWorkingProfile && resource.customWorkingProfile) {
          workingHours = resource.customWorkingProfile[dayKey] || 0;
        } else {
          workingHours = settings.globalWorkingProfile?.[dayKey] || 0;
        }

        if (workingHours === 0) {
          const rangeStart = new Date(day);
          rangeStart.setHours(0, 0, 0, 0);
          const rangeEnd = new Date(day);
          rangeEnd.setHours(23, 59, 59, 999);
          const dayStr = day.toISOString().split('T')[0];

          if (groupingMode === 'project-resource') {
            const projectsForResource = new Set<string>();
            filteredBookings.forEach(booking => {
              if (String(booking.resourceId) === String(resource.id) && booking.projectId) {
                projectsForResource.add(booking.projectId);
              }
            });
            if (projectsForResource.size === 0) {
              ranges.push({
                id: `non-working-${resource.id}-${dayStr}`,
                resourceId: String(resource.id),
                name: '',
                startDate: rangeStart,
                endDate: rangeEnd,
                cls: 'custom-style',
              });
            } else {
              projectsForResource.forEach(projectId => {
                ranges.push({
                  id: `non-working-${resource.id}-${projectId}-${dayStr}`,
                  resourceId: `${projectId}-${resource.id}`,
                  name: '',
                  startDate: rangeStart,
                  endDate: rangeEnd,
                  cls: 'custom-style',
                });
              });
            }
          } else if (groupingMode === 'role-resource') {
            const roleName = resource.role || 'Unassigned';
            ranges.push({
              id: `non-working-${resource.id}-${roleName}-${dayStr}`,
              resourceId: `${roleName}-${resource.id}`,
              name: '',
              startDate: rangeStart,
              endDate: rangeEnd,
              cls: 'custom-style',
            });
          } else {
            ranges.push({
              id: `non-working-${resource.id}-${dayStr}`,
              resourceId: resource.id,
              name: '',
              startDate: rangeStart,
              endDate: rangeEnd,
              cls: 'custom-style',
            });
          }
        }
      });
    });

    return ranges;
  }, [
    filteredData.filteredResources, filteredData.filteredBookings, settings.absenceColor,
    settings.publicHolidayColor,
    settings.showAbsence, settings.showPublicHolidays, settings.globalWorkingProfile,
    groupingMode, selectedDate, calendarView, absences,
  ]);

  // ── Time Ranges (global, non-resource-specific) ──────────────────────
  // Non-business hours in Day view are rendered as global timeRanges
  // (column-spanning) rather than per-resource resourceTimeRanges.
  const timeRanges = useMemo(() => {
    const ranges: Array<{
      id: string;
      name: string;
      startDate: Date;
      endDate: Date;
      cls?: string;
    }> = [];

    if (calendarView !== 'day') return ranges;

    const bizStartRaw = settings.businessHoursStart ?? 9;
    const bizEndRaw = settings.businessHoursEnd ?? 17;
    const visStartRaw = settings.visibleHoursStart ?? 7;
    const visEndRaw = settings.visibleHoursEnd ?? 19;

    const toHM = (v: number) => ({ h: Math.floor(v), m: Math.round((v - Math.floor(v)) * 60) });
    const bizStartHM = toHM(bizStartRaw);
    const bizEndHM = toHM(bizEndRaw);
    const visStartHM = toHM(visStartRaw);
    const visEndHM = toHM(visEndRaw);

    const validDate = selectedDate instanceof Date && !isNaN(selectedDate.getTime())
      ? selectedDate
      : new Date();
    const dayDate = new Date(validDate);

    // Before business hours: visStart → bizStart
    if (visStartRaw < bizStartRaw) {
      const rangeStart = new Date(dayDate);
      rangeStart.setHours(visStartHM.h, visStartHM.m, 0, 0);
      const rangeEnd = new Date(dayDate);
      rangeEnd.setHours(bizStartHM.h, bizStartHM.m, 0, 0);
      ranges.push({
        id: 'non-biz-am',
        name: '',
        startDate: rangeStart,
        endDate: rangeEnd,
        cls: 'custom-style',
      });
    }

    // After business hours: bizEnd → visEnd
    if (bizEndRaw < visEndRaw) {
      const rangeStart = new Date(dayDate);
      rangeStart.setHours(bizEndHM.h, bizEndHM.m, 0, 0);
      const rangeEnd = new Date(dayDate);
      rangeEnd.setHours(visEndHM.h, visEndHM.m, 0, 0);
      ranges.push({
        id: 'non-biz-pm',
        name: '',
        startDate: rangeStart,
        endDate: rangeEnd,
        cls: 'custom-style',
      });
    }

    return ranges;
  }, [
    settings.businessHoursStart, settings.businessHoursEnd,
    settings.visibleHoursStart, settings.visibleHoursEnd,
    selectedDate, calendarView,
  ]);

  // Initialize component
  useEffect(() => {
    
    // Use setTimeout to ensure the component renders after the initial mount
    const timer = setTimeout(() => {
      setIsInitialized(true);
    }, 0);
    
    return () => clearTimeout(timer);
  }, []); // Only run once on mount

  // ── Legacy localStorage cleanup ──────────────────────────────────────
  // Remove any leftover localStorage background task keys from the previous
  // client-side approach. All recurring booking creation is now handled
  // server-side via the batch API endpoint.
  useEffect(() => {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('eb-bg-task-')) {
          keysToRemove.push(key);
        }
      }
      for (const key of keysToRemove) {
        localStorage.removeItem(key);
      }
      if (keysToRemove.length > 0) {
      }
    } catch { /* ignore */ }
  }, []);

  // Reset zoom level when changing views
  // Day view defaults to 1 (15-minute intervals), others default to 0
  useEffect(() => {
    setZoomLevel(calendarView === 'day' ? 1 : 0);
  }, [calendarView]);


  // ── What-if visual overlay ────────────────────────────────────────────────
  // Ghost tiles and source-booking highlighting are handled entirely inside
  // BryntumSchedulerTimeline via its whatIfScenario prop + eventStore API.
  // A lightweight CSS stylesheet is still injected here for the moving/ghost
  // CSS classes that Bryntum applies to the rendered event elements.

  // For assign-free: auto-open the booking modal with the free resource pre-selected
  const whatIfAssignOpenedRef = useRef(false);
  useEffect(() => {
    if (
      whatIfScenario?.type === 'assign-free' &&
      !whatIfAssignOpenedRef.current &&
      resources.length > 0
    ) {
      const freeResource = resources.find(
        (r) => String(r.id) === String(whatIfScenario.freeResourceId)
      );
      if (freeResource) {
        whatIfAssignOpenedRef.current = true;
        const tid = window.setTimeout(() => {
          setPreselectedResource(freeResource);
          setPreselectedDate(whatIfScenario.targetWeekStart);
          setBookingModalMode('create');
          setEditingBooking(undefined);
          setIsBookingModalOpen(true);
        }, 400);
        return () => window.clearTimeout(tid);
      }
    }
    if (!whatIfScenario) {
      whatIfAssignOpenedRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whatIfScenario, resources]);

  // ── Plan Preview: auto-select view + centre on booking range ────────────────
  // Rules:
  //   ≤ 7 days  → Weekly view
  //   8–31 days → Monthly view
  //   ≥ 32 days → Quarterly view
  // selectedDate is set to the midpoint of the range so the bookings are
  // centred / immediately visible without manual scrolling.
  useEffect(() => {
    if (!planPreviewScenario) return;
    const slots = planPreviewScenario.scenario.slots;
    if (slots.length === 0) return;
    try {
      const startDates = slots.map(s => parseISO(s.startDate));
      const endDates   = slots.map(s => parseISO(s.endDate));

      const earliest = startDates.reduce((min, d) => (d < min ? d : min));
      const latest   = endDates.reduce((max, d)   => (d > max ? d : max));

      if (isNaN(earliest.getTime()) || isNaN(latest.getTime())) return;

      // Total span in whole days (inclusive: same-day = 1 day)
      const spanDays = Math.round(
        (latest.getTime() - earliest.getTime()) / (1000 * 60 * 60 * 24)
      ) + 1;

      // Pick the most appropriate view
      const targetView: CalendarView =
        spanDays <= 7  ? 'week'    :
        spanDays <= 31 ? 'month'   :
                         'quarter';

      // Centre selectedDate in the middle of the booking range
      const midpoint = new Date(
        (earliest.getTime() + latest.getTime()) / 2
      );

      setCalendarView(targetView);
      setSelectedDate(midpoint);
    } catch (_e) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planPreviewScenario]);

  // ── Tour: open/close booking form on demand ─────────────────────────────────
  // MUST be above the isInitialized early-return so hook count is stable
  useEffect(() => {
    const open = (e: Event) => {
      const detail = (e as CustomEvent).detail as {
        projectId?: string;
        requiredRole?: string;
        status?: string;
        durationType?: string;
        description?: string;
        startDate?: Date;
        endDate?: Date;
      } | undefined;
      setBookingModalMode('create');
      setEditingBooking(undefined);
      setPreselectedStartTime(undefined);
      setPreselectedEndTime(undefined);
      setPreselectedResource(undefined);
      if (detail) {
        setPreselectedDate(detail.startDate);
        setPreselectedEndDate(detail.endDate);
        setPreselectedBookingData({
          durationType: (detail.durationType as 'full-day' | 'half-day' | 'hourly') ?? 'full-day',
          projectId: detail.projectId,
          status: detail.status as BookingStatus | undefined,
          requiredRole: detail.requiredRole,
          description: detail.description,
        });
      } else {
        setPreselectedDate(undefined);
        setPreselectedEndDate(undefined);
        setPreselectedBookingData({ durationType: 'full-day' });
      }
      setIsBookingModalOpen(true);
    };
    const close = () => setIsBookingModalOpen(false);
    window.addEventListener('tour:open-booking-form', open);
    window.addEventListener('tour:close-booking-form', close);
    return () => {
      window.removeEventListener('tour:open-booking-form', open);
      window.removeEventListener('tour:close-booking-form', close);
    };
  }, []);

  // ── Plan Preview ghost bookings ──────────────────────────────────────────────
  // Convert proposed slots into ephemeral Booking objects (never saved) so the
  // Bryntum timeline can render them as a semi-transparent overlay.
  // NOTE: this useMemo MUST live before the isInitialized early-return so that
  // hook call order is stable across renders (Rules of Hooks).
  const previewGhostBookings = useMemo((): Booking[] => {
    if (!planPreviewScenario) return [];
    return planPreviewScenario.scenario.slots.flatMap(slot => {
      const dailyBreakdown = slot.dailyBreakdown;
      if (dailyBreakdown && dailyBreakdown.length > 0) {
        // One ghost per day — always midnight-to-midnight so the bar fills the
        // full day cell at every zoom level (day / week / month / quarter).
        return dailyBreakdown.map((day, idx) => ({
          id: `plan-preview-${slot.id}-day-${idx}`,
          resourceId: slot.resourceId,
          resourceIds: [slot.resourceId],
          projectId: planPreviewScenario.requestId,
          projectName: planPreviewScenario.requestProjectName,
          description: `${planPreviewScenario.requestRole} — Proposed`,
          startTime: `${day.date}T00:00:00`,
          endTime: `${day.date}T23:59:59`,
          color: 'var(--accent-teal-500)',
          durationType: 'full-day' as const,
          status: 'planned' as const,
          priority: 'medium' as const,
          billingClassification: 'billable' as const,
          workType: 'onsite' as const,
          notifyReminders: false,
          isPlanPreview: true,
        } as Booking & { isPlanPreview: boolean }));
      }
      // No daily breakdown — one ghost for the whole slot
      return [{
        id: `plan-preview-${slot.id}`,
        resourceId: slot.resourceId,
        resourceIds: [slot.resourceId],
        projectId: planPreviewScenario.requestId,
        projectName: planPreviewScenario.requestProjectName,
        description: `${planPreviewScenario.requestRole} — Proposed (${slot.hours}h)`,
        startTime: `${slot.startDate}T00:00:00`,
        endTime: `${slot.endDate}T23:59:59`,
        color: 'var(--accent-teal-500)',
        durationType: 'full-day' as const,
        status: 'planned' as const,
        priority: 'medium' as const,
        billingClassification: 'billable' as const,
        workType: 'onsite' as const,
        notifyReminders: false,
        isPlanPreview: true,
      } as Booking & { isPlanPreview: boolean }];
    });
  }, [planPreviewScenario]);

  // Combined resource time ranges (non-working days, absences, bank holidays)
  const combinedResourceTimeRanges = resourceTimeRanges;

  // Show loading state during initialization — but skip when the full-page
  // TimelineLoadingOverlay is already visible (first load in live mode).
  if (!isInitialized && hasCompletedFirstLoad.current) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading calendar...</p>
        </div>
      </div>
    );
  }

  const handlePrevious = () => {
    setSelectedDate((prev) => {
      switch (calendarView) {
        case 'day':
          return subDays(prev, 1);
        case 'week':
          return subWeeks(prev, 1);
        case 'month':
          return subMonths(prev, 1);
        case 'quarter':
          return subQuarters(prev, 1);
        case 'year':
          return subYears(prev, 1);
        default:
          return prev;
      }
    });
  };

  const handleNext = () => {
    setSelectedDate((prev) => {
      switch (calendarView) {
        case 'day':
          return addDays(prev, 1);
        case 'week':
          return addWeeks(prev, 1);
        case 'month':
          return addMonths(prev, 1);
        case 'quarter':
          return addQuarters(prev, 1);
        case 'year':
          return addYears(prev, 1);
        default:
          return prev;
      }
    });
  };

  const handleToday = () => {
    setSelectedDate(new Date());
  };

  // Get max zoom level based on current view
  const getMaxZoomLevel = () => {
    switch (calendarView) {
      case 'day':
        return 1; // Day view: 0 = hourly, 1 = 15 min (only 2 levels)
      case 'week':
        return 4; // Week view: 0 = daily, 1 = 12hr, 2 = 6hr, 3 = 3hr, 4 = 1hr
      default:
        return 4; // Other views can have up to 5 levels
    }
  };

  // Get zoom level labels based on current view
  const getZoomLabels = () => {
    switch (calendarView) {
      case 'day':
        return ['Min', 'Max']; // Only 2 levels for day view
      case 'week':
        return ['Min', 'Low', 'Med', 'High', 'Max'];
      default:
        return ['Min', 'Low', 'Med', 'High', 'Max'];
    }
  };

  const handleZoomIn = () => {
    const maxZoom = getMaxZoomLevel();
    setZoomLevel((prev) => Math.min(maxZoom, prev + 1));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(0, prev - 1));
  };

  const handleTimezoneChange = async (timezone: string) => {
    setSelectedTimezone(timezone);
    // Persist timezone change as user preference
    await setUserTimezone(timezone);
    sonnerToast.success('Timezone Updated', {
      description: `Timeline is now displaying times in ${timezone}. This will be used for new bookings.`,
    });
  };


  const handleCreateBooking = () => {
    // Reset preselected values when using the button
    setBookingModalMode('create');
    setEditingBooking(undefined);
    const today = new Date();
    setPreselectedDate(today);
    setPreselectedEndDate(today);

    // If "Default to logged-in resource" is enabled, pre-select the current user's resource
    const shouldDefaultToLoggedIn = settings.userDefaultToLoggedInResource ?? settings.defaultToLoggedInResource;
    if (shouldDefaultToLoggedIn && user?.id) {
      const loggedInResource = resources.find(r => String(r.id) === String(user.id));
      setPreselectedResource(loggedInResource ?? undefined);
    } else {
      setPreselectedResource(undefined);
    }
    
    // Scenario 4a: If in day view, populate start/end times from business hours settings
    // Scenario 1: If not in day view, set duration type to 'full-day'
    if (calendarView === 'day') {
      // Day view: use business hours from admin settings
      const startHour = settings.businessHoursStart ?? 9;
      const endHour = settings.businessHoursEnd ?? 17;
      setPreselectedStartTime({ hour: startHour, minute: 0 });
      setPreselectedEndTime({ hour: endHour, minute: 0 });
      setPreselectedBookingData({ durationType: 'hourly' });
    } else {
      // Non-day view: full-day booking
      setPreselectedStartTime(undefined);
      setPreselectedEndTime(undefined);
      setPreselectedBookingData({ durationType: 'full-day' });
    }
    
    setIsBookingModalOpen(true);
  };

  const openEditModal = (booking: Booking, scope: 'this' | 'all-future' | null, contextResourceId?: string) => {
    setEditScope(scope);
    setBookingModalMode('edit');
    setEditingBooking(booking);
    setEditContextResourceId(contextResourceId);
    setPreselectedResource(undefined);
    setPreselectedDate(undefined);
    setPreselectedEndDate(undefined);
    setPreselectedStartTime(undefined);
    setPreselectedEndTime(undefined);
    setPreselectedBookingData(undefined);
    setIsBookingModalOpen(true);
  };

  const handleEditBooking = (booking: Booking, contextResourceId?: string) => {

    // Store the contextResourceId for recurring booking edit flow
    setEditContextResourceId(contextResourceId);

    // If the booking is part of a recurrence group, check if it has future bookings
    if (booking.recurrenceGroupId) {
      const bookingStart = new Date(booking.startTime);
      const futureBookingsInGroup = allBookings.filter(b => {
        if (b.recurrenceGroupId !== booking.recurrenceGroupId) return false;
        if (String(b.id) === String(booking.id)) return false;
        return new Date(b.startTime) > bookingStart;
      });

      if (futureBookingsInGroup.length === 0) {
        // Last occurrence — open directly in edit mode (no dialog)
        openEditModal(booking, 'this', contextResourceId);
      } else {
        // Has future bookings — show confirmation dialog
        setBookingToEdit(booking);
        setShowEditConfirm(true);
      }
    } else {
      // Non-recurring booking — open directly
      openEditModal(booking, null, contextResourceId);
    }
  };

  const handleEditThisBooking = () => {
    setShowEditConfirm(false);
    if (bookingToEdit) {
      openEditModal(bookingToEdit, 'this', editContextResourceId);
    }
    setBookingToEdit(null);
  };

  const handleEditAllFutureBookings = () => {
    setShowEditConfirm(false);
    if (bookingToEdit) {
      openEditModal(bookingToEdit, 'all-future', editContextResourceId);
    }
    setBookingToEdit(null);
  };

  const handleCancelEditConfirm = () => {
    setShowEditConfirm(false);
    setBookingToEdit(null);
  };

  const handleDeleteBooking = (booking: Booking) => {
    setBookingToDelete(booking);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (bookingToDelete) {
      deleteBooking(bookingToDelete.id);
      removeBookingFromCache(bookingToDelete.id);
      sonnerToast.success('Booking Deleted', {
        description: 'The booking has been successfully deleted.',
      });
    }
    setShowDeleteConfirm(false);
    setBookingToDelete(null);
  };

  // Delete only the selected recurring booking occurrence
  const handleDeleteOnlyThis = () => {
    if (bookingToDelete) {
      deleteBooking(bookingToDelete.id);
      removeBookingFromCache(bookingToDelete.id);
      sonnerToast.success('Booking Deleted', {
        description: 'The selected booking occurrence has been deleted.',
      });
    }
    setShowDeleteConfirm(false);
    setBookingToDelete(null);
  };

  // Delete the selected booking and all future occurrences in the same recurrence group
  const handleDeleteAllFuture = async () => {
    if (!bookingToDelete || !bookingToDelete.recurrenceGroupId) return;

    const groupId = bookingToDelete.recurrenceGroupId;
    const selectedStartTime = new Date(bookingToDelete.startTime).getTime();

    // Start from the occurrences currently rendered on the timeline (`bookings` =
    // the date-range fetch). `allBookings` (context state) must NOT be used here:
    // in live mode it is not populated with the full booking set, so filtering it
    // yields zero matches and nothing gets deleted. `bookings` always contains the
    // occurrences the user can actually see.
    const byId = new Map<string, { id: string; startTime: string }>();
    for (const b of bookings) {
      if (b.recurrenceGroupId === groupId) {
        byId.set(String(b.id), { id: String(b.id), startTime: b.startTime });
      }
    }

    // Augment with the full series from the backend so occurrences outside the
    // visible date range are also deleted. This only adds to the set — if the
    // request fails we still delete every occurrence that is currently visible.
    if (currentMode === 'live') {
      try {
        const fetched = await bookingsApi.getByRecurrenceGroupId(groupId);
        for (const b of fetched) {
          byId.set(String(b.id), { id: String(b.id), startTime: b.startTime });
        }
      } catch (err) {
        console.error('Failed to fetch recurrence group bookings; using visible occurrences only:', err);
      }
    }

    // Find all bookings in the same recurrence group that are at or after the selected booking
    const futureBookings = Array.from(byId.values()).filter(
      b => new Date(b.startTime).getTime() >= selectedStartTime
    );


    let successCount = 0;
    let failCount = 0;
    for (const booking of futureBookings) {
      try {
        await deleteBooking(booking.id);
        removeBookingFromCache(booking.id);
        successCount++;
      } catch (err) {
        console.error(`❌ Failed to delete booking ${booking.id}:`, err);
        failCount++;
      }
    }

    sonnerToast.success('Recurring Bookings Deleted', {
      description: `Successfully deleted ${successCount} booking${successCount !== 1 ? 's' : ''}.${failCount > 0 ? ` ${failCount} failed.` : ''}`,
    });

    if (currentMode === 'live') refetchLiveBookings();

    setShowDeleteConfirm(false);
    setBookingToDelete(null);
  };

  // Delete only the selected resource's booking from a multi-resource booking.
  // With the single-booking model, we remove this resource from the booking's
  // resourceIds and update rather than deleting the whole booking.
  const handleDeleteForThisResource = async () => {
    if (!bookingToDelete) return;

    try {
      const updatedResourceIds = bookingToDelete.resourceIds
        ? bookingToDelete.resourceIds.filter(rid => String(rid) !== String(bookingToDelete.resourceId))
        : [];

      if (updatedResourceIds.length === 0) {
        // Last resource — delete the booking entirely
        await deleteBooking(bookingToDelete.id);
        removeBookingFromCache(bookingToDelete.id);
      } else {
        // Update the booking to remove this resource; set primary to the next available
        await updateBooking(String(bookingToDelete.id), {
          resourceId: updatedResourceIds[0],
          resourceIds: updatedResourceIds,
        });
      }

      sonnerToast.success('Booking Deleted', {
        description: 'The booking has been deleted for the selected resource.',
      });
      if (currentMode === 'live') refetchLiveBookings();
    } catch (err) {
      console.error('Failed to delete booking for this resource:', err);
      sonnerToast.error('Failed to delete booking');
    }

    setShowDeleteConfirm(false);
    setBookingToDelete(null);
  };

  // Delete booking for all resources in a multi-resource booking.
  // With the single-booking model, just delete the one booking record.
  const handleDeleteForAllResources = async () => {
    if (!bookingToDelete) return;

    try {
      await deleteBooking(bookingToDelete.id);
      removeBookingFromCache(bookingToDelete.id);

      sonnerToast.success('Booking Deleted', {
        description: 'Successfully deleted booking for all resources.',
      });
    } catch (err) {
      console.error(`Failed to delete booking ${bookingToDelete.id}:`, err);
      sonnerToast.error('Failed to delete booking');
    }

    if (currentMode === 'live') refetchLiveBookings();
    setShowDeleteConfirm(false);
    setBookingToDelete(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setBookingToDelete(null);
  };

  const handleMoveBooking = (booking: Booking) => {
    setBookingToMove(booking);
    setShowMoveDialog(true);
  };

  // ── Resolve Conflict via Batch Assignment Modal ────────────────────────────
  const handleResolveConflict = (cellBookings: Booking[]) => {
    setBookingsToResolve(cellBookings);
    setShowResolveDialog(true);
  };

  const handleResolveConfirm = async (assignments: { bookingId: string; resourceId: string }[]) => {
    let successCount = 0;
    for (const { bookingId, resourceId } of assignments) {
      try {
        await updateBooking(bookingId, { resourceId });
        successCount++;
      } catch { /* continue with remaining */ }
    }
    if (successCount > 0) {
      sonnerToast.success(`${successCount} conflict${successCount !== 1 ? 's' : ''} resolved`, {
        description: `${successCount} booking${successCount !== 1 ? 's' : ''} successfully reallocated.`,
      });
    }
    if (successCount < assignments.length) {
      sonnerToast.error(`${assignments.length - successCount} reassignment(s) failed`, {
        description: 'Please try again or edit those bookings manually.',
      });
    }
    setShowResolveDialog(false);
    setBookingsToResolve([]);
  };

  const handleConfirmMove = async (bookingId: string | number, newDate: Date, newResourceId: string) => {
    if (!bookingToMove) return;

    try {
      // Parse the original start and end times
      const oldStartTime = new Date(bookingToMove.startTime);
      const oldEndTime = new Date(bookingToMove.endTime);
      
      // Calculate the date difference (in days)
      const oldDate = new Date(oldStartTime);
      oldDate.setHours(0, 0, 0, 0);
      const targetDate = new Date(newDate);
      targetDate.setHours(0, 0, 0, 0);
      const dateDiff = targetDate.getTime() - oldDate.getTime();

      // Create new start and end times by adding the date difference
      const newStartTime = new Date(oldStartTime.getTime() + dateDiff);
      const newEndTime = new Date(oldEndTime.getTime() + dateDiff);

      const updatedBooking: Partial<Booking> = {
        ...bookingToMove,
        resourceId: newResourceId,
        startTime: newStartTime.toISOString(),
        endTime: newEndTime.toISOString(),
      };

      await updateBooking(String(bookingId), updatedBooking);
      if (currentMode === 'live') refetchLiveBookings();
      
      sonnerToast.success('Booking moved', {
        description: 'The booking has been successfully moved.',
      });
    } catch (error) {
      console.error('Error moving booking:', error);
      sonnerToast.error('Failed to move booking', {
        description: 'An error occurred while moving the booking.',
      });
    } finally {
      setShowMoveDialog(false);
      setBookingToMove(null);
    }
  };

  const handleCancelMove = () => {
    setShowMoveDialog(false);
    setBookingToMove(null);
  };

  const handleDuplicateBooking = (booking: Booking) => {
    // Create a copy of the booking without the ID
    const duplicatedBooking: Omit<Booking, 'id'> = {
      resourceId: booking.resourceId,
      projectId: booking.projectId,
      projectName: booking.projectName,
      costCentreId: booking.costCentreId,
      description: booking.description,
      startTime: booking.startTime,
      endTime: booking.endTime,
      color: booking.color,
      durationType: booking.durationType,
      halfDayPeriod: booking.halfDayPeriod,
      recurrence: booking.recurrence,
      billingClassification: booking.billingClassification,
      workType: booking.workType,
      taskDescription: booking.taskDescription,
      bookingNotes: booking.bookingNotes,
      status: booking.status,
      priority: booking.priority,
      attachments: booking.attachments,
      notifyReminders: booking.notifyReminders,
    };
    
    addBooking(duplicatedBooking);
    sonnerToast.success('Booking Duplicated', {
      description: 'A copy of the booking has been created.',
    });
  };

  const handleStatusChange = async (booking: Booking, newStatus: string, statusColor: string) => {
    try {
      if (filters.bookingStatuses.length > 0 && !filters.bookingStatuses.includes(newStatus)) {
        updateFilter('bookingStatuses', [...filters.bookingStatuses, newStatus]);
      }
      
      // Preserve color tag override — only fall back to status color when no tag is active
      const hasColorTag = settings.colorOverrides?.some(co => co.color === booking.color);
      await updateBooking(booking.id, {
        ...booking,
        status: newStatus as Booking['status'],
        color: hasColorTag ? booking.color : statusColor,
      });
      
      if (currentMode === 'live') refetchLiveBookings();
      
      const statusConfig = settings.statusConfigs.find(s => String(s.id) === String(newStatus));
      const statusDisplayName = statusConfig?.customText || statusConfig?.text || newStatus;
      
      sonnerToast.success('Status Updated', {
        description: `Booking status changed to "${statusDisplayName}"`,
      });
    } catch (error) {
      console.error('Error updating booking status:', error);
      sonnerToast.error('Failed to Update Status', {
        description: 'Please try again.',
      });
    }
  };

  const handleSlotClick = (resource: Resource, date: Date, startTime: { hour: number; minute: number }, bookingData?: Partial<Booking>) => {
    setPreselectedResource(resource);
    setPreselectedDate(date);
    
    // Use projectId from bookingData if provided (from BryntumSchedulerTimeline in project-resource mode)
    // This ensures the project is pre-selected when clicking on a day in project & resource view
    const projectIdToPreselect = bookingData?.projectId;
    
    // Scenario 2: Timeline click - set duration type based on calendar view
    // Day view: hourly booking with specific times
    // Non-day view: full-day booking (ignore Bryntum's time, use 00:00:00)
    if (calendarView === 'day') {
      // Day view: Add 15 minutes for end time
      let endHour: number;
      let endMinute: number;
      endMinute = startTime.minute + 15;
      if (endMinute >= 60) {
        endHour = startTime.hour + 1;
        endMinute = endMinute - 60;
      } else {
        endHour = startTime.hour;
      }
      
      setPreselectedStartTime(startTime);
      setPreselectedEndTime({ hour: endHour, minute: endMinute });
      setPreselectedBookingData({ 
        durationType: 'hourly',
        projectId: projectIdToPreselect 
      });
    } else {
      // Non-day views: full-day booking - don't set times (use 00:00:00)
      setPreselectedStartTime(undefined);
      setPreselectedEndTime(undefined);
      setPreselectedBookingData({ 
        durationType: 'full-day',
        projectId: projectIdToPreselect 
      });
    }
    
    setIsBookingModalOpen(true);
  };

  const handleDragSelect = (
    resource: Resource,
    date: Date,
    startTime: { hour: number; minute: number },
    endTime: { hour: number; minute: number },
    bookingData?: Partial<Booking>,
    endDate?: Date
  ) => {
    setPreselectedResource(resource);
    setPreselectedDate(date);
    setPreselectedEndDate(endDate || date);
    setPreselectedStartTime(startTime);
    setPreselectedEndTime(endTime);
    
    // Extract project ID if in project-resource mode
    let projectIdToPreselect: string | undefined;
    if (groupingMode === 'project-resource' && String(resource.id).includes('-')) {
      // In project-resource mode, resource IDs are in format "projectId-resourceId"
      const parts = String(resource.id).split('-');
      if (parts.length >= 2 && !String(resource.id).startsWith('group-')) {
        projectIdToPreselect = parts[0];
      }
    }
    
    // Scenario 3 & 4b: Timeline drag - set duration type based on calendar view
    // Day view (Scenario 4b): hourly booking with times from timeline
    // Non-day view (Scenario 3): full-day booking (ignore Bryntum's time, use 00:00:00)
      if (calendarView === 'day' || bookingData?.durationType === 'hourly') {
        // Hourly selection (either Day view or zoomed hourly in other views): use dragged times
        setPreselectedStartTime(startTime);
        setPreselectedEndTime(endTime);
        setPreselectedBookingData({ 
          ...bookingData, 
          durationType: 'hourly',
          projectId: projectIdToPreselect || bookingData?.projectId 
        });
      } else {
        // Non-hourly in non-day views: treat as full-day, ignore times
        setPreselectedStartTime(undefined);
        setPreselectedEndTime(undefined);
        setPreselectedBookingData({ 
          ...bookingData, 
          durationType: 'full-day',
          projectId: projectIdToPreselect || bookingData?.projectId 
        });
      }
    
    setIsBookingModalOpen(true);
  };

  const handlePasteBooking = (
    resource: Resource,
    date: Date,
    startTime: { hour: number; minute: number },
    bookingData?: Booking
  ) => {
    if (!bookingData) {
      sonnerToast.error('No Booking to Paste', {
        description: 'Please copy a booking first.',
      });
      return;
    }
    
    // Calculate new start and end times based on the paste location
    const originalStart = new Date(bookingData.startTime);
    const originalEnd = new Date(bookingData.endTime);
    const durationMs = originalEnd.getTime() - originalStart.getTime();
    
    // For non-hourly bookings, use UTC dates at midnight (same logic as add booking)
    let finalStartTime: Date;
    let finalEndTime: Date;
    
    if (bookingData.durationType === 'full-day' || bookingData.durationType === 'half-day-am' || bookingData.durationType === 'half-day-pm') {
      // Extract the local date from the paste location
      const pasteYear = date.getFullYear();
      const pasteMonth = date.getMonth();
      const pasteDay = date.getDate();
      
      // Create UTC dates at midnight
      finalStartTime = new Date(Date.UTC(pasteYear, pasteMonth, pasteDay, 0, 0, 0, 0));
      
      // Calculate duration in days and add to start date
      const durationDays = Math.ceil(durationMs / (1000 * 60 * 60 * 24));
      finalEndTime = new Date(Date.UTC(pasteYear, pasteMonth, pasteDay + durationDays, 0, 0, 0, 0));
    } else {
      // For hourly bookings, preserve the original booking's time-of-day
      const newStartTime = new Date(date);
      newStartTime.setHours(originalStart.getHours(), originalStart.getMinutes(), 0, 0);
      finalStartTime = newStartTime;
      finalEndTime = new Date(newStartTime.getTime() + durationMs);
    }
    
    // Create a copy of the booking with new resource and times
    const pastedBooking: Omit<Booking, 'id'> = {
      resourceId: resource.id,
      projectId: bookingData.projectId,
      projectName: bookingData.projectName,
      costCentreId: bookingData.costCentreId,
      description: bookingData.description,
      startTime: formatDateAsLocalISO(finalStartTime),
      endTime: formatDateAsLocalISO(finalEndTime),
      color: bookingData.color,
      durationType: bookingData.durationType,
      halfDayPeriod: bookingData.halfDayPeriod,
      recurrence: bookingData.recurrence,
      billingClassification: bookingData.billingClassification,
      workType: bookingData.workType,
      taskDescription: bookingData.taskDescription,
      bookingNotes: bookingData.bookingNotes,
      status: bookingData.status,
      priority: bookingData.priority,
      attachments: bookingData.attachments,
      notifyReminders: bookingData.notifyReminders,
      bookingTypeId: bookingData.bookingTypeId,
      customer: bookingData.customer,
      location: bookingData.location,
      details: bookingData.details,
      bookingReference: bookingData.bookingReference,
      userChar: bookingData.userChar,
      userNumber: bookingData.userNumber,
      userFlag: bookingData.userFlag,
      userDate: bookingData.userDate,
      userTime: bookingData.userTime,
      userNotes: bookingData.userNotes,
    };
    
    addBooking(pastedBooking);
    
    // Show success toast
    sonnerToast.success('Booking Pasted', {
      description: `"${bookingData.projectName || bookingData.description || 'Booking'}" has been pasted to ${resource.name}.`,
    });
    
    // Clear the copied booking after paste
    setCopiedBooking(null);
  };

  const handleQuickAddBooking = (resource: Resource, date: Date, startTime?: string, endTime?: string) => {
    setQuickAddPreselectedDate(date);
    setQuickAddPreselectedResource(resource);
    if (startTime) {
      setQuickAddDefaultStartTime(startTime);
    }
    if (endTime) {
      setQuickAddDefaultEndTime(endTime);
    }
    // Extract project ID if in project-resource mode and store it for Quick Add modal
    let projectIdToPreselect: string | undefined;
    if (groupingMode === 'project-resource' && resource.id.includes('-')) {
      // In project-resource mode, resource IDs are in format "projectId-resourceId"
      const parts = resource.id.split('-');
      if (parts.length >= 2 && !resource.id.startsWith('group-')) {
        projectIdToPreselect = parts[0];
      }
    }
    // Store project ID in preselected booking data for Quick Add modal to use
    setPreselectedBookingData({ projectId: projectIdToPreselect });
    
    setIsQuickAddModalOpen(true);
  };

  const handleQuickAddBookingSave = async (data: QuickAddBookingData) => {
    
    try {
      // Check if we have a preselected resource
      if (!quickAddPreselectedResource) {
        sonnerToast.error('No Resource Selected', {
          description: 'Please select a resource to create a booking.',
        });
        return;
      }
      
      // Get the default booking type from settings
      const defaultBookingTypeId = settings.quickAddDefault;
      const defaultBookingType = settings.bookingTypes.find(bt => String(bt.id) === String(defaultBookingTypeId));
      
      // If no default booking type is set or found, use the first active booking type
      const bookingTypeId = defaultBookingType?.id || settings.bookingTypes.find(bt => bt.active)?.id || settings.bookingTypes[0]?.id;
      
      if (!bookingTypeId) {
        sonnerToast.error('Booking Type not assigned', {
          description: 'No booking type has been assigned. Please contact your administrator to configure booking type permissions.',
        });
        return;
      }
      
      // Prepare start and end times
      let startTime: string;
      let endTime: string;
      
      if (data.isFullDay) {
        // Full day booking - use UTC dates at midnight (00:00:00 UTC)
        // Extract local date values and create UTC dates
        const startDateObj = new Date(data.startDate);
        const startYear = startDateObj.getFullYear();
        const startMonth = startDateObj.getMonth();
        const startDay = startDateObj.getDate();
        
        const endDateObj = new Date(data.endDate);
        const endYear = endDateObj.getFullYear();
        const endMonth = endDateObj.getMonth();
        const endDay = endDateObj.getDate();
        
        // Create UTC dates at midnight - end date + 1 for exclusive end
        const utcStartDate = new Date(Date.UTC(startYear, startMonth, startDay, 0, 0, 0, 0));
        const utcEndDate = new Date(Date.UTC(endYear, endMonth, endDay + 1, 0, 0, 0, 0));
        
        startTime = utcStartDate.toISOString();
        endTime = utcEndDate.toISOString();
      } else {
        // Timed booking - combine date with time
        const [startHour, startMinute] = data.startTime!.split(':').map(Number);
        const startDate = new Date(data.startDate);
        startDate.setHours(startHour, startMinute, 0, 0);
        
        const [endHour, endMinute] = data.endTime!.split(':').map(Number);
        const endDate = new Date(data.endDate);
        endDate.setHours(endHour, endMinute, 0, 0);
        
        startTime = startDate.toISOString();
        endTime = endDate.toISOString();
      }
      
      // Get the preselected project ID only in project-resource view
      let selectedProject = null;
      
      // In project-resource view, use the preselected project
      if (groupingMode === 'project-resource' && preselectedBookingData?.projectId) {
        selectedProject = projectDetails.find(p => String(p.id) === String(preselectedBookingData.projectId));
        
        if (!selectedProject) {
          sonnerToast.error('No Projects Available', {
            description: 'Please create a project before adding bookings.',
          });
          return;
        }
      }
      // In resource view, leave project unset (null)
      
      // Extract the actual resource ID (in project-resource or customer mode, IDs are composite)
      let actualResourceId = quickAddPreselectedResource.id;
      if (groupingMode === 'project-resource' && actualResourceId.includes('-') && !actualResourceId.startsWith('group-')) {
        const parts = actualResourceId.split('-');
        if (parts.length >= 2) {
          actualResourceId = parts.slice(1).join('-'); // Get everything after the first dash
        }
      } else if (groupingMode === 'customer' && actualResourceId.includes('-') && !actualResourceId.startsWith('group-') && !actualResourceId.startsWith('customer-')) {
        // Match by suffix to handle hyphens in customer names
        const matchedRes = resources.find(r => actualResourceId.endsWith(`-${String(r.id)}`));
        if (matchedRes) {
          actualResourceId = String(matchedRes.id);
        } else {
          const dashIdx = actualResourceId.indexOf('-');
          if (dashIdx !== -1) actualResourceId = actualResourceId.slice(dashIdx + 1);
        }
      }
      
      // Create booking for the preselected resource only
      const newBooking: Omit<Booking, 'id'> = {
        bookingTypeId: bookingTypeId != null ? String(bookingTypeId) : undefined, // Use the default booking type from settings
        resourceId: actualResourceId,
        resourceIds: [actualResourceId],
        projectId: selectedProject?.id != null ? String(selectedProject.id) : null,
        projectName: selectedProject?.name || '',
        clientId: selectedProject?.clientId ?? null, // Auto-populate client from project
        customer: selectedProject?.clientName || undefined,
        description: data.description || '',
        startTime,
        endTime,
        timezone: selectedTimezone,
        color: selectedProject?.color || '#3b82f6',
        durationType: data.isFullDay ? 'full-day' : 'hourly',
        billingClassification: 'billable',
        workType: 'onsite',
        status: 'confirmed',
        priority: 'medium',
        notifyReminders: false,
      };

      await addBooking(newBooking);
      
      // Close the modal
      setIsQuickAddModalOpen(false);
      
      // Reset preselected resource
      setQuickAddPreselectedResource(undefined);
      
      // Show success message
      const bookingTypeName = defaultBookingType?.name || 'booking';
      sonnerToast.success('Booking Created', {
        description: `Successfully created ${bookingTypeName} for ${quickAddPreselectedResource.name}.`,
      });
      
    } catch (error) {
      console.error('❌ Error creating quick booking:', error);
      sonnerToast.error('Failed to create booking', {
        description: 'Please try again.',
      });
    }
  };

  const handleSaveBooking = async (booking: Omit<Booking, 'id'>) => {
    setIsSavingBooking(true);
    
    // ── Recurring booking creation ──────────────────────────────────────
    // The modal sets booking.recurrence (pattern, interval, etc.) but does NOT
    // generate individual occurrences. We detect recurrence here, generate dates,
    // create the first booking immediately, and process the rest in background.
    const hasRecurrence = booking.recurrence && booking.recurrence.pattern && booking.recurrence.pattern !== 'none';

    if (hasRecurrence) {
      const recurrenceGroupId = crypto.randomUUID();

      // Build the first (parent) booking — keeps recurrence settings for editing
      const parentBooking: Omit<Booking, 'id'> = {
        ...booking,
        recurrenceGroupId,
        isRecurrenceParent: true,
      };

      // Generate remaining occurrence dates (excludes the first/parent date)
      const occurrenceDates = generateRecurrenceDates(booking, booking as Booking);

      // Check if the original booking's start date falls within the repeat
      // dialog's date range. If the series start date was set later than the
      // booking start, the first booking should be skipped.
      const bookingStart = new Date(booking.startTime);
      const seriesStart = booking.recurrence?.startDate
        ? new Date(booking.recurrence.startDate)
        : undefined;
      const seriesEnd = booking.recurrence?.endDate
        ? new Date(booking.recurrence.endDate)
        : undefined;

      const bookingStartNorm = new Date(bookingStart.getFullYear(), bookingStart.getMonth(), bookingStart.getDate());
      // Resolve resource for non-working day check on the parent booking
      const parentResourceId = booking.resourceId || booking.resourceIds?.[0];
      const parentResource = parentResourceId ? resources.find(r => String(r.id) === String(parentResourceId)) : undefined;
      const firstBookingInRange =
        (!seriesStart || bookingStartNorm >= new Date(seriesStart.getFullYear(), seriesStart.getMonth(), seriesStart.getDate())) &&
        (!seriesEnd || bookingStartNorm <= new Date(seriesEnd.getFullYear(), seriesEnd.getMonth(), seriesEnd.getDate())) &&
        !isNonWorkingDayForResource(parentResource, bookingStart);

      try {
        // Step 1: Create the first booking immediately (only if within range)
        let parentBookingId: number | undefined;
        if (firstBookingInRange) {
          if (parentBooking.resourceIds && parentBooking.resourceIds.length > 0) {
            const multiResourceParent: Omit<Booking, 'id'> = {
              ...parentBooking,
              resourceId: parentBooking.resourceIds[0],
              resourceIds: parentBooking.resourceIds,
            };
            const createdParent = await addBooking(multiResourceParent);
            if (createdParent) parentBookingId = Number(createdParent.id);
          } else {
            const createdParent = await addBooking(parentBooking);
            if (createdParent) parentBookingId = Number(createdParent.id);
          }
        }

        const immediateCount = firstBookingInRange ? 1 : 0;

        // Single toast: confirm first booking + inform about background creation
        if (occurrenceDates.length > 0) {
          const bgCount = occurrenceDates.length;
          const msg = immediateCount > 0
            ? `1 booking created successfully. ${bgCount} recurring booking${bgCount !== 1 ? 's' : ''} will be created in the background.`
            : `${bgCount} recurring booking${bgCount !== 1 ? 's' : ''} will be created in the background.`;
          sonnerToast.success(msg);

          // Step 2: Build all child bookings and send to server in a single batch request.
          const originalStart = new Date(booking.startTime);
          const originalEnd = new Date(booking.endTime);
          const duration = originalEnd.getTime() - originalStart.getTime();

          const childBookings: Omit<Booking, 'id'>[] = occurrenceDates.map(date => {
            const newStart = new Date(date);
            newStart.setHours(originalStart.getHours(), originalStart.getMinutes(), originalStart.getSeconds(), originalStart.getMilliseconds());
            const newEnd = new Date(newStart.getTime() + duration);
            return {
              ...booking,
              recurrence: undefined,
              startTime: formatDateAsLocalISO(newStart),
              endTime: formatDateAsLocalISO(newEnd),
              recurrenceGroupId,
              isRecurrenceParent: false,
              parentBookingId: parentBookingId || undefined,
            };
          });

          // Fire-and-forget: server handles all creation even if the browser closes
          createBatchBookings(childBookings).then(() => {
            if (currentMode === 'live') refetchLiveBookings();
          }).catch(err => {
            console.error('Server batch creation failed:', err);
          });
        } else {
          sonnerToast.success('Booking Created', {
            description: 'The booking has been successfully created.',
          });
        }

        // Reset preselected values after save
        setPreselectedResource(undefined);
        setPreselectedDate(undefined);
        setPreselectedEndDate(undefined);
        setPreselectedStartTime(undefined);
        setPreselectedEndTime(undefined);
        setPreselectedBookingData(undefined);
      } catch (error) {
        console.error('❌ Error creating recurring booking:', error);
        sonnerToast.error('Failed to create recurring booking', {
          description: 'Please try again.',
        });
      } finally {
        setIsSavingBooking(false);
      }
      return;
    }
    
    try {
      // Check if multiple resources are selected
      if (booking.resourceIds && booking.resourceIds.length > 0) {
        
        // Create a single booking with the first resource as the primary resourceId.
        // The addBooking function creates BookingResource entries for all resourceIds,
        // so we only need one booking record mapped to multiple resources.
        const multiResourceBooking: Omit<Booking, 'id'> = {
          ...booking,
          resourceId: booking.resourceIds[0], // Primary resource
          resourceIds: booking.resourceIds, // All resources for BookingResource entries
        };
        
        await addBooking(multiResourceBooking);
        
        sonnerToast.success('Booking Created', {
          description: `Successfully created booking across ${booking.resourceIds.length} resource${booking.resourceIds.length > 1 ? 's' : ''}.`,
        });
      } else {
        // Single resource booking (legacy behavior)
        await addBooking(booking);
        sonnerToast.success('Booking Created', {
          description: 'The booking has been successfully created.',
        });
      }
      
      // Refetch live bookings so utilisation recalculates with server data
      if (currentMode === 'live') refetchLiveBookings();

      // Reset preselected values after save
      setPreselectedResource(undefined);
      setPreselectedDate(undefined);
      setPreselectedEndDate(undefined);
      setPreselectedStartTime(undefined);
      setPreselectedEndTime(undefined);
      setPreselectedBookingData(undefined);
    } catch (error) {
      console.error('❌ Error adding booking:', error);
      sonnerToast.error('Failed to create booking', {
        description: 'Please try again.',
      });
    } finally {
      setIsSavingBooking(false);
    }
  };

  // Helper: check if recurrence options have changed between original and updated booking
  const hasRecurrenceOptionsChanged = (original: Booking, updated: Partial<Booking>): boolean => {
    if (!updated.recurrence && !original.recurrence) return false;
    if (!updated.recurrence || !original.recurrence) return true;
    const origRec = original.recurrence;
    const newRec = updated.recurrence;
    return (
      origRec.pattern !== newRec.pattern ||
      origRec.interval !== newRec.interval ||
      origRec.endType !== newRec.endType ||
      origRec.endDate !== newRec.endDate ||
      origRec.occurrences !== newRec.occurrences
    );
  };

  const handleUpdateBooking = async (id: string | number, booking: Partial<Booking>) => {
    setIsSavingBooking(true);
    
    try {
      // If status is being changed and there's an active status filter, add the new status to the filter
      if (booking.status && filters.bookingStatuses.length > 0 && !filters.bookingStatuses.includes(booking.status)) {
        updateFilter('bookingStatuses', [...filters.bookingStatuses, booking.status]);
      }
      
      // Find the original booking
      const originalBooking = bookings.find(b => String(b.id) === String(id));
      
      if (!originalBooking) {
        console.error('❌ Original booking not found:', id);
        sonnerToast.error('Booking not found', {
          description: 'Could not find the original booking.',
        });
        return;
      }

      // Determine if recurrence was removed (user cleared recurring info)
      const recurrenceRemoved = originalBooking.recurrenceGroupId && 
        (!booking.recurrence || booking.recurrence.pattern === 'none');

      // ── Case A: Non-recurring booking → becoming recurring ──
      if (!originalBooking.recurrenceGroupId && booking.recurrence && booking.recurrence.pattern !== 'none') {

        const recurrenceGroupId = crypto.randomUUID();
        const updatedOriginal: Partial<Booking> = {
          ...booking,
          recurrenceGroupId,
          isRecurrenceParent: true,
        };
        await updateBooking(String(id), updatedOriginal);

        const occurrences = generateRecurrenceDates(booking, originalBooking);
        
        if (occurrences.length > 0) {
          sonnerToast.success(
            `Booking updated successfully. ${occurrences.length} recurring booking${occurrences.length !== 1 ? 's' : ''} will be created in the background.`
          );

          const mergedBooking = { ...originalBooking, ...booking };
          const originalStart = new Date(mergedBooking.startTime);
          const originalEnd = new Date(mergedBooking.endTime);
          const duration = originalEnd.getTime() - originalStart.getTime();

          const childBookings: Omit<Booking, 'id'>[] = occurrences.map(date => {
            const newStart = new Date(date);
            newStart.setHours(originalStart.getHours(), originalStart.getMinutes(), originalStart.getSeconds(), originalStart.getMilliseconds());
            const newEnd = new Date(newStart.getTime() + duration);
            return {
              ...mergedBooking,
              recurrence: undefined,
              startTime: formatDateAsLocalISO(newStart),
              endTime: formatDateAsLocalISO(newEnd),
              recurrenceGroupId,
              isRecurrenceParent: false,
            };
          });

          createBatchBookings(childBookings).then(() => {
            if (currentMode === 'live') refetchLiveBookings();
          }).catch(err => {
            console.error('❌ Server batch creation (from update) failed:', err);
          });
        } else {
          sonnerToast.success('Booking Updated', {
            description: 'The booking has been successfully updated.',
          });
        }

        if (currentMode === 'live') refetchLiveBookings();
        return;
      }

      // ── Case B: Recurring booking — editScope = 'this' (only this booking) ──
      if (originalBooking.recurrenceGroupId && editScope === 'this') {

        if (recurrenceRemoved) {
          // User removed recurrence → convert this booking to a normal (non-recurring) booking
          await updateBooking(String(id), {
            ...booking,
            recurrenceGroupId: undefined,
            isRecurrenceParent: false,
            parentBookingId: undefined,
            recurrence: undefined,
          });
        } else {
          // Update only this booking with the changed details
          await updateBooking(String(id), booking);
        }

        sonnerToast.success('Booking Updated', {
          description: 'The booking has been successfully updated.',
        });
        if (currentMode === 'live') refetchLiveBookings();
        return;
      }

      // ── Case C: Recurring booking — editScope = 'all-future' ──
      if (originalBooking.recurrenceGroupId && editScope === 'all-future') {

        const bookingStart = new Date(originalBooking.startTime);
        // Get all future bookings in the group (including the current one)
        const futureGroupBookings = allBookings.filter(b => {
          if (b.recurrenceGroupId !== originalBooking.recurrenceGroupId) return false;
          return new Date(b.startTime) >= bookingStart;
        });
        // Get past bookings in the group (before the current one)
        const pastGroupBookings = allBookings.filter(b => {
          if (b.recurrenceGroupId !== originalBooking.recurrenceGroupId) return false;
          return new Date(b.startTime) < bookingStart;
        });


        if (recurrenceRemoved) {
          // User removed recurrence → remove recurring info from this + all future bookings
          for (const futureBooking of futureGroupBookings) {
            try {
              await updateBooking(futureBooking.id, {
                ...booking,
                recurrenceGroupId: undefined,
                isRecurrenceParent: false,
                parentBookingId: undefined,
                recurrence: undefined,
              });
            } catch (err) {
              console.error(`❌ Failed to update booking ${futureBooking.id}:`, err);
            }
          }
          sonnerToast.success('Bookings are updated successfully.');
          if (currentMode === 'live') refetchLiveBookings();
          return;
        }

        // Check if recurrence options have changed
        const recurrenceChanged = hasRecurrenceOptionsChanged(originalBooking, booking);

        if (recurrenceChanged && booking.recurrence) {
          // Recurrence options changed → delete future bookings, create new ones

          // Delete all future bookings except the edited one
          for (const futureBooking of futureGroupBookings) {
            if (String(futureBooking.id) !== String(id)) {
              try {
                await deleteBooking(futureBooking.id);
              } catch (err) {
                console.error(`❌ Failed to delete future booking ${futureBooking.id}:`, err);
              }
            }
          }

          // Create a new recurrence group for the edited booking + new children
          const newGroupId = crypto.randomUUID();
          await updateBooking(String(id), {
            ...booking,
            recurrenceGroupId: newGroupId,
            isRecurrenceParent: true,
          });

          // Generate new recurring occurrences
          const occurrences = generateRecurrenceDates(booking, originalBooking);

          if (occurrences.length > 0) {
            const mergedBooking = { ...originalBooking, ...booking };
            const originalStart = new Date(mergedBooking.startTime);
            const originalEnd = new Date(mergedBooking.endTime);
            const duration = originalEnd.getTime() - originalStart.getTime();

            const childBookings: Omit<Booking, 'id'>[] = occurrences.map(date => {
              const newStart = new Date(date);
              newStart.setHours(originalStart.getHours(), originalStart.getMinutes(), originalStart.getSeconds(), originalStart.getMilliseconds());
              const newEnd = new Date(newStart.getTime() + duration);
              return {
                ...mergedBooking,
                recurrence: undefined,
                startTime: formatDateAsLocalISO(newStart),
                endTime: formatDateAsLocalISO(newEnd),
                recurrenceGroupId: newGroupId,
                isRecurrenceParent: false,
              };
            });

            createBatchBookings(childBookings).then(() => {
              if (currentMode === 'live') refetchLiveBookings();
            }).catch(err => {
              console.error('❌ Server batch recreation failed:', err);
            });
          }

          sonnerToast.success('Bookings are updated successfully.');
          if (currentMode === 'live') refetchLiveBookings();
          return;
        } else {
          // Recurrence options NOT changed → update this + all future bookings with new details

          const originalStart = new Date(originalBooking.startTime);
          const originalEnd = new Date(originalBooking.endTime);
          const updatedStart = booking.startTime ? new Date(booking.startTime) : originalStart;
          const updatedEnd = booking.endTime ? new Date(booking.endTime) : originalEnd;
          const startDiff = updatedStart.getTime() - originalStart.getTime();
          const durationDiff = (updatedEnd.getTime() - updatedStart.getTime()) - (originalEnd.getTime() - originalStart.getTime());

          let updatedCount = 0;
          for (const futureBooking of futureGroupBookings) {
            if (String(futureBooking.id) === String(id)) {
              await updateBooking(String(id), booking);
              updatedCount++;
            } else {
              const groupUpdate: Partial<Booking> = { ...booking };
              
              if (startDiff !== 0 || durationDiff !== 0) {
                const siblingStart = new Date(futureBooking.startTime);
                if (startDiff !== 0) {
                  siblingStart.setHours(updatedStart.getHours(), updatedStart.getMinutes(), updatedStart.getSeconds(), updatedStart.getMilliseconds());
                }
                const newDuration = updatedEnd.getTime() - updatedStart.getTime();
                const newSiblingEnd = new Date(siblingStart.getTime() + newDuration);
                groupUpdate.startTime = formatDateAsLocalISO(siblingStart);
                groupUpdate.endTime = formatDateAsLocalISO(newSiblingEnd);
              } else {
                delete groupUpdate.startTime;
                delete groupUpdate.endTime;
              }
              
              try {
                await updateBooking(futureBooking.id, groupUpdate);
                updatedCount++;
              } catch (err) {
                console.error(`❌ Failed to update future booking ${futureBooking.id}:`, err);
              }
            }
          }

          sonnerToast.success('Bookings are updated successfully.');
          if (currentMode === 'live') refetchLiveBookings();
          return;
        }
      }
      
      // ── Case D: Multi-resource booking update (non-recurring) ──
      // With the single-booking model, just update the one booking record.
      // BookingResource entries are managed by the updateBooking context function.
      if (booking.resourceIds && booking.resourceIds.length > 1) {
        await updateBooking(String(id), booking);

        if (currentMode === 'live') refetchLiveBookings();
        sonnerToast.success('Booking Updated', {
          description: `Successfully updated booking across ${booking.resourceIds.length} resources.`,
        });
      } else {
        // ── Case E: Single resource, non-recurring update ──
        await updateBooking(String(id), booking);
        if (currentMode === 'live') refetchLiveBookings();
        sonnerToast.success('Booking Updated', {
          description: 'The booking has been successfully updated.',
        });
      }
    } catch (error) {
      console.error('❌ Error updating booking:', error);
      sonnerToast.error('Failed to update booking', {
        description: 'Please try again.',
      });
    } finally {
      setIsSavingBooking(false);
    }
  };

  // Helper: check if a date is a non-working day for a given resource
  const isNonWorkingDayForResource = (resource: Resource | undefined, date: Date): boolean => {
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
    const dayKey = dayNames[date.getDay()];

    if (resource?.useCustomWorkingProfile && resource.customWorkingProfile) {
      const hours = resource.customWorkingProfile[dayKey] || 0;
      return hours === 0;
    }

    const hours = settings.globalWorkingProfile?.[dayKey] || 0;
    return hours === 0;
  };

  // Generate recurrence dates based on recurrence settings
  const generateRecurrenceDates = (booking: Partial<Booking>, originalBooking: Booking): Date[] => {
    const recurrence = booking.recurrence || originalBooking.recurrence;
    if (!recurrence || recurrence.pattern === 'none') {
      return [];
    }

    const bookingStartDate = booking.startTime ? new Date(booking.startTime) : new Date(originalBooking.startTime);
    const interval = recurrence.interval || 1;
    const dates: Date[] = [];
    const maxOccurrences = recurrence.occurrences || 52;
    const seriesStartDate = recurrence.startDate ? new Date(recurrence.startDate) : undefined;
    const seriesEndDate = recurrence.endDate ? new Date(recurrence.endDate) : undefined;

    // Resolve the resource for non-working day filtering
    const resourceId = booking.resourceId || booking.resourceIds?.[0] || originalBooking.resourceId || originalBooking.resourceIds?.[0];
    const resource = resourceId ? resources.find(r => String(r.id) === String(resourceId)) : undefined;
    const filterNonWorkingDays = (d: Date[]) => d.filter(date => !isNonWorkingDayForResource(resource, date));

    // Safety: if endDate is in the past or before startDate, use a sensible default
    if (recurrence.endType === 'on-date' && seriesEndDate && seriesEndDate <= bookingStartDate) {
      const fallbackEnd = addMonths(bookingStartDate, 3);
      for (let i = 1; i < maxOccurrences; i++) {
        let nextDate: Date;
        switch (recurrence.pattern) {
          case 'daily': nextDate = addDays(bookingStartDate, i * interval); break;
          case 'weekly': nextDate = addDays(bookingStartDate, i * 7 * interval); break;
          case 'monthly': nextDate = addMonths(bookingStartDate, i * interval); break;
          default: return dates;
        }
        if (nextDate > fallbackEnd) break;
        dates.push(nextDate);
      }
      return filterNonWorkingDays(dates);
    }

    const withinLimit = (d: Date, count: number): boolean => {
      if (seriesEndDate) {
        const dNorm = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const endNorm = new Date(seriesEndDate.getFullYear(), seriesEndDate.getMonth(), seriesEndDate.getDate());
        if (dNorm > endNorm) return false;
      }
      if (recurrence.endType === 'after' && recurrence.occurrences && count >= recurrence.occurrences - 1) return false;
      return true;
    };

    // Filter helper: only include dates that fall on or after the series start
    const afterSeriesStart = (d: Date): boolean => {
      if (!seriesStartDate) return true;
      const dNorm = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const sNorm = new Date(seriesStartDate.getFullYear(), seriesStartDate.getMonth(), seriesStartDate.getDate());
      return dNorm >= sNorm;
    };

    if (recurrence.pattern === 'weekly' && recurrence.daysOfWeek && recurrence.daysOfWeek.length > 0) {
      // Weekly with specific days of week selected
      const dayNameToIndex: Record<string, number> = {
        sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
        thursday: 4, friday: 5, saturday: 6,
      };
      const targetDayIndices = recurrence.daysOfWeek
        .map(d => dayNameToIndex[d.toLowerCase()])
        .filter(i => i !== undefined)
        .sort((a, b) => a - b);

      if (targetDayIndices.length === 0) return dates;

      // Start scanning from the day after the booking start date
      let currentDate = addDays(bookingStartDate, 1);
      let weekCounter = 0;
      let lastWeekNumber = -1;

      while (dates.length < maxOccurrences * 7) {
        const currentDayOfWeek = currentDate.getDay();
        // Track week boundaries for interval > 1
        const weeksSinceStart = Math.floor(
          (currentDate.getTime() - bookingStartDate.getTime()) / (7 * 86400000)
        );

        if (weeksSinceStart !== lastWeekNumber) {
          lastWeekNumber = weeksSinceStart;
          weekCounter = weeksSinceStart;
        }

        // Only include dates from weeks that match the interval
        if (weekCounter % interval === 0 && targetDayIndices.includes(currentDayOfWeek)) {
          if (!withinLimit(currentDate, dates.length)) break;
          if (afterSeriesStart(currentDate)) {
            dates.push(new Date(currentDate));
          }
        }

        // If we're past the end date, stop scanning
        if (seriesEndDate) {
          const cNorm = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
          const endNorm = new Date(seriesEndDate.getFullYear(), seriesEndDate.getMonth(), seriesEndDate.getDate());
          if (cNorm > endNorm) break;
        }

        currentDate = addDays(currentDate, 1);
        // Safety: don't scan more than 5 years
        if (currentDate.getTime() - bookingStartDate.getTime() > 5 * 365 * 86400000) break;
      }
    } else if (recurrence.pattern === 'monthly') {
      // Monthly recurrence
      if (recurrence.monthlyRecurrenceType === 'relative-weekday' && recurrence.relativeWeekday) {
        // Option 2: "Monthly on the Xth Weekday"
        const { occurrence, weekday } = recurrence.relativeWeekday;
        const dayNameToIndex: Record<string, number> = {
          sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
          thursday: 4, friday: 5, saturday: 6,
        };
        const targetDayIndex = dayNameToIndex[weekday.toLowerCase()] ?? 0;
        const occurrenceMap: Record<string, number> = {
          first: 1, second: 2, third: 3, fourth: 4, last: -1,
        };
        const targetOccurrence = occurrenceMap[occurrence] ?? 1;

        for (let i = 1; i < maxOccurrences + 1; i++) {
          const targetMonth = new Date(bookingStartDate);
          targetMonth.setMonth(targetMonth.getMonth() + i * interval);
          targetMonth.setDate(1);

          const year = targetMonth.getFullYear();
          const month = targetMonth.getMonth();

          let foundDate: Date | null = null;
          if (targetOccurrence === -1) {
            // Last occurrence: scan backwards from end of month
            const lastDay = new Date(year, month + 1, 0).getDate();
            for (let d = lastDay; d >= 1; d--) {
              const candidate = new Date(year, month, d);
              if (candidate.getDay() === targetDayIndex) {
                foundDate = candidate;
                break;
              }
            }
          } else {
            // Nth occurrence: scan forwards
            let count = 0;
            for (let d = 1; d <= 31; d++) {
              const candidate = new Date(year, month, d);
              if (candidate.getMonth() !== month) break;
              if (candidate.getDay() === targetDayIndex) {
                count++;
                if (count === targetOccurrence) {
                  foundDate = candidate;
                  break;
                }
              }
            }
          }

          if (foundDate) {
            if (!withinLimit(foundDate, dates.length)) break;
            if (afterSeriesStart(foundDate)) {
              dates.push(foundDate);
            }
          }
        }
      } else {
        // Option 1: "Monthly on day N" (default)
        const targetDay = recurrence.dayOfMonth ?? bookingStartDate.getDate();
        for (let i = 1; i < maxOccurrences + 1; i++) {
          const targetMonth = new Date(bookingStartDate);
          targetMonth.setMonth(targetMonth.getMonth() + i * interval);
          // Clamp to last day of month if target day exceeds month length
          const lastDayOfMonth = new Date(
            targetMonth.getFullYear(),
            targetMonth.getMonth() + 1,
            0
          ).getDate();
          targetMonth.setDate(Math.min(targetDay, lastDayOfMonth));

          if (!withinLimit(targetMonth, dates.length)) break;
          if (afterSeriesStart(targetMonth)) {
            dates.push(targetMonth);
          }
        }
      }
    } else {
      // Daily or simple weekly (no specific days)
      for (let i = 1; i < maxOccurrences; i++) {
        let nextDate: Date;
        switch (recurrence.pattern) {
          case 'daily':
            nextDate = addDays(bookingStartDate, i * interval);
            break;
          case 'weekly':
            nextDate = addDays(bookingStartDate, i * 7 * interval);
            break;
          default:
            return filterNonWorkingDays(dates);
        }

        if (!withinLimit(nextDate, dates.length)) break;
        if (afterSeriesStart(nextDate)) {
          dates.push(nextDate);
        }
      }
    }

    return filterNonWorkingDays(dates);
  };

  const handleBookingDragEnd = (
    booking: Booking,
    originalResource: Resource,
    newResourceId: string | number,
    newStartTime: Date,
    newEndTime: Date,
    isResize: boolean = false
  ) => {

    // Convert newResourceId to string for string operations (handles INT IDs after schema change)
    const newResourceIdStr = String(newResourceId);

    // VALIDATION: Prevent dropping bookings on group/parent nodes
    // Group nodes have IDs that start with "group-", "customer-", etc.
    if (newResourceIdStr.startsWith('group-') || newResourceIdStr.startsWith('customer-')) {
      
      // Cancel drag and show error message
      cancelDrag();
      
      sonnerToast.error('Invalid Drop Location', {
        description: 'Please drop the booking on a specific resource.',
      });
      
      return;
    }

    // Find resource by comparing as strings (handles INT IDs after schema change)
    const newResource = resources.find(r => String(r.id) === newResourceIdStr);
    if (!newResource) {
      // Cancel drag if resource not found
      cancelDrag();
      return;
    }

    const stripTzSuffix = (t: string) => t.replace('Z', '').replace(/[+-]\d{2}:\d{2}$/, '');
    const originalStartTime = new Date(stripTzSuffix(booking.startTime));
    const originalEndTime = new Date(stripTzSuffix(booking.endTime));

    // For resize operations: pin the non-resized edge to the original stored
    // value.  Bryntum may round or shift the unchanged edge during snap
    // (e.g. 23:59:59.999 → next-day midnight), which causes unintended
    // date drift.  Compare deltas to detect which edge the user dragged.
    let pinnedStartTime = newStartTime;
    let pinnedEndTime = newEndTime;
    if (isResize) {
      const startDelta = Math.abs(newStartTime.getTime() - originalStartTime.getTime());
      const endDelta = Math.abs(newEndTime.getTime() - originalEndTime.getTime());
      if (startDelta <= endDelta) {
        // End was resized → preserve original start exactly
        pinnedStartTime = new Date(originalStartTime);
      } else {
        // Start was resized → preserve original end exactly
        pinnedEndTime = new Date(originalEndTime);
      }
    }

    // Apply date/time logic based on duration type
    // For non-hourly bookings: store start at 00:00:00, end at 23:59:59.999
    // For hourly bookings: use exact times from Bryntum
    let adjustedStartTime: Date;
    let adjustedEndTime: Date;
    
    // Determine if this is a full-day type booking (not hourly)
    const isFullDayType = booking.durationType === 'full-day' || 
                          booking.durationType === 'half-day-am' || 
                          booking.durationType === 'half-day-pm' ||
                          booking.durationType === 'half-day';
    
    const isHalfDayType = booking.durationType === 'half-day' ||
                          booking.durationType === 'half-day-am' ||
                          booking.durationType === 'half-day-pm';

    const originalStart = new Date(stripTzSuffix(booking.startTime));
    const originalEnd = new Date(stripTzSuffix(booking.endTime));
    const hasSpecificTime = originalStart.getHours() !== 0 || originalStart.getMinutes() !== 0;
    
    // Determine if the timeline is currently showing hourly granularity based on
    // calendarView and zoomLevel:
    // - Day view: always hourly (zoomLevel 0 = hourly, zoomLevel 1 = 15-min)
    // - Week/Month view: zoomLevel 0 = daily, zoomLevel >= 1 = hourly
    // - Quarter/Year view: never hourly
    const isTimelineHourlyGranularity =
      calendarView === 'day' ||
      ((calendarView === 'week' || calendarView === 'month') && zoomLevel >= 1);

    // Treat as hourly if: durationType is 'hourly' OR (durationType is not full-day AND has specific times)
    // Also treat half-day bookings as hourly so their original start time and duration are preserved
    // Also treat resized full-day bookings as hourly when in a zoomed-in hourly view
    // so that the resize respects the snap interval instead of extending to full day
    const isHourlyBooking = booking.durationType === 'hourly' || (!isFullDayType && hasSpecificTime) || isHalfDayType || (isResize && isFullDayType && isTimelineHourlyGranularity);

    if (!isHourlyBooking) {
      // For non-hourly (full-day) bookings: store start at 00:00:00, end at 23:59:59.999
      // Extract the LOCAL date values from the pinned times
      const startYear = pinnedStartTime.getFullYear();
      const startMonth = pinnedStartTime.getMonth();
      const startDay = pinnedStartTime.getDate();
      
      adjustedStartTime = new Date(startYear, startMonth, startDay, 0, 0, 0, 0);
      
      // Bryntum uses EXCLUSIVE end dates: a 1-day booking on March 13 has
      // endDate = March 14 00:00. We need to convert to the INCLUSIVE last day
      // at 23:59:59.999 so the stored endTime reflects the actual last day.
      if (pinnedEndTime.getHours() === 0 && pinnedEndTime.getMinutes() === 0 && pinnedEndTime.getSeconds() === 0) {
        const inclusiveEnd = new Date(pinnedEndTime);
        inclusiveEnd.setDate(inclusiveEnd.getDate() - 1);
        adjustedEndTime = new Date(inclusiveEnd.getFullYear(), inclusiveEnd.getMonth(), inclusiveEnd.getDate(), 23, 59, 59, 999);
      } else {
        const endYear = pinnedEndTime.getFullYear();
        const endMonth = pinnedEndTime.getMonth();
        const endDay = pinnedEndTime.getDate();
        adjustedEndTime = new Date(endYear, endMonth, endDay, 23, 59, 59, 999);
      }
    } else {
      // For hourly bookings: Use the times directly from Bryntum without any conversion
      // Whatever time the user sees/selects is what gets stored
      
      if (isResize || calendarView === 'day') {
        // Resize operation OR Day view drag - use exact pinned times
        adjustedStartTime = new Date(pinnedStartTime);
        adjustedEndTime = new Date(pinnedEndTime);

        // For half-day resizes in day-granularity views (week/month/etc.),
        // Bryntum uses exclusive end dates (e.g. next-day midnight for a single
        // day).  Convert to the inclusive last-day end so the booking doesn't
        // visually bleed into the next day.
        if (isResize && isHalfDayType && calendarView !== 'day') {
          if (adjustedEndTime.getHours() === 0 && adjustedEndTime.getMinutes() === 0 && adjustedEndTime.getSeconds() === 0) {
            const inclusiveEnd = new Date(adjustedEndTime);
            inclusiveEnd.setDate(inclusiveEnd.getDate() - 1);
            adjustedEndTime = new Date(inclusiveEnd.getFullYear(), inclusiveEnd.getMonth(), inclusiveEnd.getDate(), 23, 59, 59, 999);
          }
        }
      } else {
        // Non-day view DRAG - preserve original time, change only the date
        const durationMs = originalEnd.getTime() - originalStart.getTime();
        
        // Get the original time components
        const originalHours = originalStart.getHours();
        const originalMinutes = originalStart.getMinutes();
        
        // Create new date with the dropped date but original time
        adjustedStartTime = new Date(pinnedStartTime);
        adjustedStartTime.setHours(originalHours, originalMinutes, 0, 0);
        
        // End time is start time + original duration
        adjustedEndTime = new Date(adjustedStartTime.getTime() + durationMs);
      }
    }

    // Check if anything actually changed (compare as strings for type safety)
    const hasChanged = 
      String(originalResource.id) !== newResourceIdStr ||
      originalStartTime.getTime() !== adjustedStartTime.getTime() ||
      originalEndTime.getTime() !== adjustedEndTime.getTime();

    if (!hasChanged) {
      // Cancel drag if nothing changed
      cancelDrag();
      return;
    }

    // Prepare the update object with adjusted times
    // Preserve all existing booking fields to prevent data loss (e.g., status)
    // Use formatDateAsLocalISO to prevent timezone conversion (e.g., -5:30 for IST)
    // Also update resourceIds so the tooltip and form reflect the new resource
    const oldPrimaryId = String(booking.resourceId ?? '');
    const updatedResourceIds = booking.resourceIds && booking.resourceIds.length > 0
      ? booking.resourceIds.map(rid => String(rid) === oldPrimaryId ? String(newResource.id) : String(rid))
      : [String(newResource.id)];
    const bookingUpdate: Partial<Booking> = {
      ...booking,
      resourceId: newResource.id,
      resourceIds: updatedResourceIds,
      startTime: formatDateAsLocalISO(adjustedStartTime),
      endTime: formatDateAsLocalISO(adjustedEndTime),
    };

    if (calendarView === 'day') {
      bookingUpdate.durationType = 'hourly';
    } else if (isResize && isHalfDayType) {
      bookingUpdate.durationType = 'hourly';
    } else if (isResize && isFullDayType && isTimelineHourlyGranularity) {
      bookingUpdate.durationType = 'hourly';
    } else if (booking.durationType) {
      bookingUpdate.durationType = booking.durationType;
    } else if (isHourlyBooking) {
      bookingUpdate.durationType = 'hourly';
    }

    // When resizing a half-day or full-day booking in hourly view, clear the
    // half-day period since the booking is no longer constrained to AM/PM
    if (isResize && isHalfDayType) {
      bookingUpdate.halfDayPeriod = undefined;
    }

    cancelDrag();

    // When the resource changes via drag-and-drop, update the role to match the new resource
    const resourceChanged = String(booking.resourceId ?? '') !== String(newResource.id);
    if (resourceChanged && newResource.role) {
      bookingUpdate.requiredRole = newResource.role;
    }

    // Check if the Confirm Drag/Drop preference is enabled (user-level overrides global)
    const shouldConfirm = settings.userConfirmDragDrop ?? settings.confirmDragDrop;

    if (shouldConfirm) {
      // Show confirmation dialog with pending update details
      setPendingDragUpdate({
        booking,
        originalResource,
        newResource,
        originalStartTime,
        originalEndTime,
        newStartTime: adjustedStartTime,
        newEndTime: adjustedEndTime,
        isResize,
      });
      setShowDragConfirm(true);
    } else {
      // Apply the update directly without confirmation
      setIsSavingBooking(true);
      updateBooking(booking.id, bookingUpdate).then(() => {
        if (currentMode === 'live') refetchLiveBookings();
        sonnerToast.success(isResize ? 'Booking Resized' : 'Booking Moved', {
          description: isResize ? 'The booking has been successfully resized.' : 'The booking has been successfully moved.',
        });
      }).catch(() => {
        sonnerToast.error(isResize ? 'Failed to Resize Booking' : 'Failed to Move Booking', {
          description: 'Please try again.',
        });
      }).finally(() => {
        setIsSavingBooking(false);
      });
    }
  };

  const handleConfirmDrag= () => {
    if (!pendingDragUpdate) return;

    // newStartTime and newEndTime are already adjusted (UTC times) from handleBookingDragEnd
    const { booking, newResource, newStartTime, newEndTime, isResize: wasResize } = pendingDragUpdate;

    // Prepare the update object - times are already adjusted in handleBookingDragEnd
    // Preserve all existing booking fields to prevent data loss (e.g., status)
    // Use formatDateAsLocalISO to prevent timezone conversion (e.g., -5:30 for IST)
    // Also update resourceIds so the tooltip and form reflect the new resource
    const confirmOldPrimaryId = String(booking.resourceId ?? '');
    const confirmUpdatedResourceIds = booking.resourceIds && booking.resourceIds.length > 0
      ? booking.resourceIds.map(rid => String(rid) === confirmOldPrimaryId ? String(newResource.id) : String(rid))
      : [String(newResource.id)];
    const bookingUpdate: Partial<Booking> = {
      ...booking,
      resourceId: newResource.id,
      resourceIds: confirmUpdatedResourceIds,
      startTime: formatDateAsLocalISO(newStartTime),
      endTime: formatDateAsLocalISO(newEndTime),
    };

    const confirmIsHalfDay = booking.durationType === 'half-day' ||
                             booking.durationType === 'half-day-am' ||
                             booking.durationType === 'half-day-pm';
    const confirmIsFullDay = booking.durationType === 'full-day' ||
                             booking.durationType === 'half-day-am' ||
                             booking.durationType === 'half-day-pm' ||
                             booking.durationType === 'half-day';
    const confirmIsTimelineHourly =
      calendarView === 'day' ||
      ((calendarView === 'week' || calendarView === 'month') && zoomLevel >= 1);

    if (calendarView === 'day') {
      bookingUpdate.durationType = 'hourly';
    } else if (wasResize && confirmIsHalfDay) {
      bookingUpdate.durationType = 'hourly';
    } else if (wasResize && confirmIsFullDay && confirmIsTimelineHourly) {
      bookingUpdate.durationType = 'hourly';
    } else if (booking.durationType) {
      bookingUpdate.durationType = booking.durationType;
    }

    if (wasResize && confirmIsHalfDay) {
      bookingUpdate.halfDayPeriod = undefined;
    }

    // When the resource changes via drag-and-drop, update the role to match the new resource
    const resourceChanged = String(booking.resourceId ?? '') !== String(newResource.id);
    if (resourceChanged && newResource.role) {
      bookingUpdate.requiredRole = newResource.role;
    }

    // Update the booking - this will trigger a smooth transition
    setShowDragConfirm(false);
    setPendingDragUpdate(null);
    setIsSavingBooking(true);
    updateBooking(booking.id, bookingUpdate).then(() => {
      if (currentMode === 'live') refetchLiveBookings();
      sonnerToast.success('Booking Moved', {
        description: 'The booking has been successfully moved.',
      });
    }).catch(() => {
      sonnerToast.error('Failed to Move Booking', {
        description: 'Please try again.',
      });
    }).finally(() => {
      setIsSavingBooking(false);
    });
  };

  const handleDiscardDrag = () => {
    // Re-sync the Bryntum event store from the React-derived data so the
    // drag/resize-modified event snaps back to its original position.
    // This is more robust than revertEvent because it uses the same data
    // source (bryntumEvents) and handles grouping, timezone shifts, and
    // half-day display clamping automatically.
    if (schedulerTimelineRef.current) {
      schedulerTimelineRef.current.forceStoreRefresh();
    }
    
    // Clean up the dialog state
    setShowDragConfirm(false);
    setPendingDragUpdate(null);
    
    sonnerToast.info('Changes Discarded', {
      description: 'The booking was not moved.',
    });
  };

  const getDateLabel = () => {
    switch (calendarView) {
      case 'day':
        return format(selectedDate, 'EEEE, MMMM d, yyyy');
      case 'week':
        return `Week of ${format(selectedDate, 'MMM d, yyyy')}`;
      case 'month':
        return format(selectedDate, 'MMMM yyyy');
      case 'quarter':
        return `Q${Math.floor(selectedDate.getMonth() / 3) + 1} ${format(selectedDate, 'yyyy')}`;
      case 'year':
        return format(selectedDate, 'yyyy');
      default:
        return '';
    }
  };

  const handleDatePickerSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date);
      setIsDatePickerOpen(false);
    }
  };

  // Handler for Copy Booking from context menu
  const handleCopyBooking = (booking: Booking) => {
    setCopiedBooking(booking);
    sonnerToast.success('Booking Copied', {
      description: `"${booking.projectName || 'Booking'}" has been copied. Right-click on an empty cell to paste.`,
    });
  };

  // Handler for Add Booking from context menu - opens the booking form
  const handleAddBookingFromMenu = (resource: Resource, date: Date) => {
    setPreselectedResource(resource);
    setPreselectedDate(date);
    setPreselectedEndDate(date);
    
    // Extract project ID if in project-resource mode
    let projectIdToPreselect: string | undefined;
    if (groupingMode === 'project-resource' && resource.id.includes('-')) {
      // In project-resource mode, resource IDs are in format "projectId-resourceId"
      const parts = resource.id.split('-');
      if (parts.length >= 2 && !resource.id.startsWith('group-')) {
        projectIdToPreselect = parts[0];
      }
    }
    
    // Set duration type based on calendar view
    if (calendarView === 'day') {
      // Day view: Use the clicked time slot and add 15 minutes for end time
      const startHour = date.getHours();
      const startMinute = date.getMinutes();
      
      // Calculate end time: 15 minutes after start time
      let endHour: number;
      let endMinute: number;
      endMinute = startMinute + 15;
      if (endMinute >= 60) {
        endHour = startHour + 1;
        endMinute = endMinute - 60;
      } else {
        endHour = startHour;
      }
      
      setPreselectedStartTime({ hour: startHour, minute: startMinute });
      setPreselectedEndTime({ hour: endHour, minute: endMinute });
      setPreselectedBookingData({ 
        durationType: 'hourly',
        projectId: projectIdToPreselect 
      });
    } else {
      setPreselectedStartTime(undefined);
      setPreselectedEndTime(undefined);
      setPreselectedBookingData({ 
        durationType: 'full-day',
        projectId: projectIdToPreselect 
      });
    }
    
    setBookingModalMode('create');
    setEditingBooking(undefined);
    setIsBookingModalOpen(true);
  };

  const renderCalendarView = () => {
    // Merge preview ghosts into the filtered bookings list
    const bookingsWithPreview = planPreviewScenario
      ? [...filteredData.filteredBookings, ...previewGhostBookings]
      : filteredData.filteredBookings;

    return (
      <BryntumSchedulerTimeline
        ref={schedulerTimelineRef}
        selectedDate={selectedDate}
        calendarView={calendarView}
        zoomLevel={zoomLevel}
        selectedTimezone={selectedTimezone}
        enableTimezones={settings.enableTimezones}
        resources={filteredData.filteredResources}
        bookings={bookingsWithPreview}
        resourceTimeRanges={combinedResourceTimeRanges}
        timeRanges={timeRanges}
        groupingMode={groupingMode}

        schedulerGroups={schedulerGroups}
        statusConfigs={settings.statusConfigs}
        isHeatmapMode={isHeatmapMode}
        absences={absences}
        bankHolidays={mockBankHolidays}
        searchQuery={bookingSearchQuery}
        onEditBooking={handleEditBooking}
        onResolveConflict={handleResolveConflict}
        onDeleteBooking={handleDeleteBooking}
        onDuplicateBooking={handleDuplicateBooking}
        onMoveBooking={handleMoveBooking}
        onStatusChange={handleStatusChange}
        onCopyBooking={handleCopyBooking}
        onSlotClick={handleSlotClick}
        onDragSelect={handleDragSelect}
        onBookingDragEnd={handleBookingDragEnd}
        onAddBooking={handleAddBookingFromMenu}
        onQuickAddBooking={handleQuickAddBooking}
        onPasteBooking={handlePasteBooking}
        copiedBooking={copiedBooking}
        whatIfScenario={whatIfScenario}
        bookingTypeUsages={bookingTypeUsages}
      />
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Plan Preview Banner */}
      {planPreviewScenario && (
        <div className="shrink-0 border-b bg-[var(--accent-teal-50)] border-[var(--accent-teal-200)]">
          <div className="flex items-center gap-3 px-4 py-2.5">
            {/* Icon */}
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[var(--accent-teal-100)] border border-[var(--accent-teal-300)]">
              <Eye className="h-3.5 w-3.5 text-[var(--accent-teal-700)]" />
            </div>
            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold text-[var(--accent-teal-800)]">
                  Preview mode
                </span>
                <span className="text-xs text-[var(--accent-teal-700)]">—</span>
                <span className="text-xs text-[var(--accent-teal-700)] font-medium truncate">
                  {planPreviewScenario.scenario.label}
                </span>
                <span className="text-[10px] text-[var(--accent-teal-600)]">
                  for {planPreviewScenario.requestProjectName} · {planPreviewScenario.requestRole}
                </span>
                <span className="inline-flex items-center gap-1 text-[10px] font-medium text-[var(--accent-teal-700)] bg-[var(--accent-teal-100)] border border-[var(--accent-teal-300)] px-1.5 py-0.5 rounded-md">
                  <span className="h-1.5 w-1.5 rounded-full bg-[var(--accent-teal-500)] animate-pulse shrink-0" />
                  {planPreviewScenario.scenario.slots.length} proposed bookings shown as dashed
                </span>
              </div>
            </div>
            {/* Actions */}
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs gap-1.5 border-[var(--accent-teal-400)] text-[var(--accent-teal-700)] hover:bg-[var(--accent-teal-100)]"
                onClick={onPlanPreviewClose}
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to plan
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-[var(--accent-teal-600)] hover:bg-[var(--accent-teal-100)]"
                onClick={onPlanPreviewClose}
                title="Dismiss preview"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header Controls */}
      <div className="shrink-0 border-b bg-background" data-tour="planner-toolbar">
        <div className="flex items-center justify-between px-3 py-2 gap-2 w-full">
        <div className="flex items-center gap-2 shrink-0">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handlePrevious}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={handleToday}>
              Today
            </Button>
            <Button variant="outline" size="sm" onClick={handleNext}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          
          <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
            <PopoverTrigger asChild>
              <button
                className="text-base font-semibold min-w-[140px] max-w-[200px] text-left hover:text-primary cursor-pointer flex items-center gap-1.5 transition-colors whitespace-nowrap"
                title="Click to select a date"
              >
                {getDateLabel()}
                <CalendarIcon className="h-4 w-4 opacity-50" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleDatePickerSelect}
                defaultMonth={selectedDate}
              />
            </PopoverContent>
          </Popover>

          {/* Booking Quick Search */}
            <div className="relative w-[160px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={bookingSearchQuery}
                onChange={(e) => setBookingSearchQuery(e.target.value)}
                className="pl-9 h-8"
              />
              {bookingSearchQuery && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6"
                  onClick={() => setBookingSearchQuery('')}
                  title="Clear search"
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* Feature Feedback & Data Mode Toggle */}
          <DataModeToggle screen="calendar" />
          <FeatureFeedbackCollector feature={isHeatmapMode ? 'Heatmap' : 'Planner'} />

          {/* Booking Search Results Counter */}
          {bookingSearchQuery && (
            <Badge variant="secondary" className="h-8 px-3 text-sm font-semibold whitespace-nowrap">
              {filteredData.filteredBookings.length} / {bookings.length} Bookings
            </Badge>
          )}

          {/* Create Booking Button */}
          <Button data-tour="add-booking-btn" onClick={handleCreateBooking} size="sm" className="h-8 px-2.5 gap-1">
            <Plus className="h-4 w-4" />
            {currentMode === 'demo' ? 'Add Booking' : 'New Booking'}
          </Button>

          {/* Current Timezone Selector - Shows next to Create Booking button */}
          <CurrentTimezoneSelector
            selectedTimezone={selectedTimezone}
            onTimezoneChange={handleTimezoneChange}
          />

          {/* Zoom Controls */}
          <div className="flex items-center gap-1.5">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleZoomOut}
              disabled={calendarView === 'day' || calendarView === 'year' || calendarView === 'quarter' || zoomLevel === 0}
              className="h-8 px-2"
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground min-w-[50px] text-center">
              {getZoomLabels()[zoomLevel]}
            </span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleZoomIn}
              disabled={calendarView === 'day' || calendarView === 'year' || calendarView === 'quarter' || zoomLevel === getMaxZoomLevel()}
              className="h-8 px-2"
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
          </div>

          {/* View Selector */}
          <Select value={calendarView} onValueChange={(value) => setCalendarView(value as CalendarView)}>
            <SelectTrigger className="w-[100px] h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-[240px]" position="popper" sideOffset={4} align="start">
              <SelectItem value="day">Day</SelectItem>
              <SelectItem value="week">Week</SelectItem>
              <SelectItem value="month">Month</SelectItem>
              <SelectItem value="quarter">Quarter</SelectItem>
              <SelectItem value="year">Year</SelectItem>
            </SelectContent>
          </Select>
          {/* Filter Drawer Trigger — far right, aligns with right-side slide-in */}
          <CalendarFilterDrawerTrigger
            activeFilterCount={
              filters.departments.length +
              filters.resources.length +
              filters.roles.length +
              filters.projects.length +
              filters.costCentres.length +
              filters.bookingTypes.length +
              filters.bookingStatuses.length +
              (filters.utilisationRange[0] !== 0 || filters.utilisationRange[1] !== 100 || filters.overUtilisedOnly ? 1 : 0) +
              (hasActiveAdvancedFilters ? advancedFilters.conditions.length : 0)
            }
            onClick={() => setIsFilterTrayOpen(true)}
          />
          <HelpButton />
        </div>
        </div>
      </div>

      {/* Filter Drawer (replaces collapsible tray) */}
      <CalendarFilterDrawer
        open={isFilterTrayOpen}
        onOpenChange={setIsFilterTrayOpen}
      />

      {/* ── Burnout risk banners — rendered via BurnoutRiskPanel below (portal slot) ── */}
      {isHeatmapMode && (
        <div id="burnout-banner-slot" data-tour="whatif-panel" className="shrink-0" />
      )}

      {/* ── What-if amber banner — shown in calendar view when a scenario is active ── */}
      {whatIfScenario && (
        <div data-tour="conflict-panel" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          padding: '7px 16px',
          flexShrink: 0,
          background: 'linear-gradient(90deg, rgba(245,158,11,0.10) 0%, rgba(245,158,11,0.04) 100%)',
          borderBottom: '1px solid rgba(245,158,11,0.30)',
          borderLeft: '3px solid #f59e0b',
        }}>
          {/* Left: icon + what-if pill + description */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
              backgroundColor: '#f59e0b', color: '#fff', fontSize: 11, fontWeight: 800,
            }}>⟳</span>
            <span style={{
              display: 'inline-flex', alignItems: 'center',
              padding: '2px 9px', borderRadius: 20, flexShrink: 0,
              fontSize: 10, fontWeight: 700, letterSpacing: '0.07em',
              textTransform: 'uppercase' as const,
              backgroundColor: '#f59e0b', color: '#fff',
            }}>
              What-if mode
            </span>
            <span style={{ fontSize: 12, fontWeight: 500, color: '#78350f', lineHeight: 1.4, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
              {whatIfScenario.type === 'rebalance'
                ? `${whatIfScenario.projectName ?? 'Project'} — move from ${whatIfScenario.atRiskResourceName ?? '…'} to ${whatIfScenario.candidateResourceName ?? '…'} · not yet saved`
                : `${whatIfScenario.freeResourceName ?? 'Resource'} becomes free W${whatIfScenario.freeFromWeek ?? '?'} · assign new work · not yet saved`
              }
            </span>
          </div>
          {/* Right: Commit + Discard buttons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <button
              onClick={handleWhatIfCommit}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '5px 14px', borderRadius: 6, flexShrink: 0,
                fontSize: 12, fontWeight: 700, cursor: 'pointer',
                backgroundColor: '#0d9488', color: '#fff',
                border: 'none', letterSpacing: '0.01em',
                transition: 'opacity 0.15s',
                whiteSpace: 'nowrap' as const,
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.85'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
            >
              ✓ Commit changes
            </button>
            <button
              onClick={handleWhatIfDiscard}
              style={{
                display: 'inline-flex', alignItems: 'center',
                padding: '5px 14px', borderRadius: 6, flexShrink: 0,
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
                backgroundColor: 'transparent', color: '#ef4444',
                border: '1px solid #ef4444',
                transition: 'background 0.15s',
                whiteSpace: 'nowrap' as const,
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(239,68,68,0.06)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; }}
            >
              Discard scenario
            </button>
          </div>
        </div>
      )}

      {/* Calendar View + (optionally) Burnout Suggestions Panel side-by-side */}
      <div className="flex-1 flex overflow-hidden" data-tour={isHeatmapMode ? 'heatmap-grid' : 'planner-calendar'}>
        {/* Main calendar grid */}
        <div
          ref={schedulerDomRef}
          className="flex-1 overflow-hidden calendar-grid-container relative"
        >
          {!isCompanyLoading && !hasCompanyAccess ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-muted-foreground text-sm">
                First create at least one company and assign the access to the user.
              </p>
            </div>
          ) : (
            <>
              {/* Grouping mode selector — absolutely positioned over Bryntum's column header.
                  Pure React inside our own DOM tree, so it's always visible and interactive. */}
              {/* Grouping Select — spans the full 45px header height so it vertically
                  centres to match the collapse button. Measurements:
                    column=240px, cell-padding-left=12px, cell-padding-right=12px
                    button=32px → button sits at right: 12px (right edge = 228px from left)
                    gap=8px → select right edge = 228-8=220px
                    select left = 8px → select width = 220-8 = 212px                    */}
              <div data-tour="planner-grouping" className="absolute z-50 flex items-center" style={{ top: groupingSelectTop, left: 8, width: 170, height: 32 }}>
                <Select
                  value={groupingMode}
                  onValueChange={(mode) => {
                    setGroupingMode(mode as typeof groupingMode);
                    setHasUserChangedGrouping(true);
                  }}
                >
                  <SelectTrigger className="h-8 w-full text-[13px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer">Customer</SelectItem>
                    <SelectItem value="project-resource">Project</SelectItem>
                    <SelectItem value="resource">Resource</SelectItem>
                    <SelectItem value="role-resource">Role</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {renderCalendarView()}
              {(isSavingBooking || (hasCompletedFirstLoad.current && (isLoadingBookings || (currentMode !== 'demo' && isContextLoading) || isGroupsLoading))) && (
                <div className="absolute inset-0 bg-background/50 backdrop-blur-[1px] z-50 flex items-center justify-center">
                  <div className="flex items-center gap-3 bg-background border rounded-lg px-5 py-3 shadow-lg">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    <span className="text-sm font-medium">{isSavingBooking ? 'Saving booking...' : (isContextLoading || isGroupsLoading) ? 'Loading data...' : 'Loading bookings...'}</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Burnout risk warnings — always active in heatmap mode.
            When whatIfEnabled + onNavigateToCalendarWithWhatIf are available,
            the suggestions panel and what-if overlay are also rendered. */}
        {isHeatmapMode && (
          <div className="contents">
            <BurnoutRiskPanelWrapper
              schedulerDomRef={schedulerDomRef}
              resources={filteredData.filteredResources}
              bookings={filteredData.filteredBookings}
              globalWorkingProfile={settings.globalWorkingProfile}
              onNavigateToCalendar={whatIfEnabled && onNavigateToCalendarWithWhatIf ? onNavigateToCalendarWithWhatIf : undefined}
            />
          </div>
        )}
      </div>

      {/* Heatmap Legend — sits flush below the grid, only in heatmap mode */}
      <HeatmapLegend visible={isHeatmapMode} />

      {/* Comprehensive Booking Modal */}
      <ComprehensiveBookingModal
        key="comprehensive-booking-modal"
        open={isBookingModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsBookingModalOpen(false);
            // Ensure any temporary/phantom events are removed from the timeline
            schedulerTimelineRef.current?.cleanupPhantomEvents?.();
            // Reset preselected values when modal closes
            setBookingModalMode('create');
            setEditingBooking(undefined);
            setEditContextResourceId(undefined);
            setPreselectedResource(undefined);
            setPreselectedDate(undefined);
            setPreselectedEndDate(undefined);
            setPreselectedStartTime(undefined);
            setPreselectedEndTime(undefined);
            setPreselectedBookingData(undefined);
          }
        }}
        onSave={handleSaveBooking}
        onUpdate={handleUpdateBooking}
        onDelete={handleDeleteBooking}
        resources={resources}
        projectDetails={projectDetails}
        costCentres={costCentres}
        existingBookings={bookings}
                preselectedResource={preselectedResource}
                preselectedDate={preselectedDate}
                preselectedEndDate={preselectedEndDate}
                preselectedStartTime={preselectedStartTime ? `${String(preselectedStartTime.hour).padStart(2, '0')}:${String(preselectedStartTime.minute).padStart(2, '0')}` : undefined}
        preselectedEndTime={preselectedEndTime ? `${String(preselectedEndTime.hour).padStart(2, '0')}:${String(preselectedEndTime.minute).padStart(2, '0')}` : undefined}
        preselectedBookingData={preselectedBookingData}
        editingBooking={editingBooking}
        editContextResourceId={editContextResourceId}
        mode={bookingModalMode}
        dataMode={currentMode}
      />

      {/* Drag Confirmation Dialog */}
      <BookingDragConfirmDialog
        open={showDragConfirm}
        booking={pendingDragUpdate?.booking || null}
        originalResource={pendingDragUpdate?.originalResource}
        newResource={pendingDragUpdate?.newResource}
        originalStartTime={pendingDragUpdate?.originalStartTime || null}
        originalEndTime={pendingDragUpdate?.originalEndTime || null}
        newStartTime={pendingDragUpdate?.newStartTime || null}
        newEndTime={pendingDragUpdate?.newEndTime || null}
        onConfirm={handleConfirmDrag}
        onDiscard={handleDiscardDrag}
      />

      {/* Quick Add Booking Modal */}
      <QuickAddBookingModal
        open={isQuickAddModalOpen}
        onClose={() => setIsQuickAddModalOpen(false)}
        onSave={handleQuickAddBookingSave}
        defaultStartTime={quickAddDefaultStartTime}
        defaultEndTime={quickAddDefaultEndTime}
        preselectedDate={quickAddPreselectedDate}
      />

      {/* Delete Booking Confirmation Dialog */}
      <DeleteBookingConfirmDialog
        open={showDeleteConfirm}
        booking={bookingToDelete}
        resource={bookingToDelete ? resources.find(r => String(r.id) === String(bookingToDelete.resourceId)) : null}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        onDeleteOnlyThis={handleDeleteOnlyThis}
        onDeleteAllFuture={handleDeleteAllFuture}
        onDeleteForThisResource={handleDeleteForThisResource}
        onDeleteForAllResources={handleDeleteForAllResources}
      />

      {/* Edit Recurring Booking Confirmation Dialog */}
      <EditBookingConfirmDialog
        open={showEditConfirm}
        onCancel={handleCancelEditConfirm}
        onEditThisBooking={handleEditThisBooking}
        onEditAllFutureBookings={handleEditAllFutureBookings}
      />

      {/* Move Booking Dialog */}
      <MoveBookingDialog
        open={showMoveDialog}
        onClose={handleCancelMove}
        booking={bookingToMove}
        resources={resources}
        onMove={handleConfirmMove}
      />

      {/* Resolve Conflict — Batch Assignment Modal */}
      {bookingsToResolve.length > 0 && (
        <AIBatchSuggestModal
          open={showResolveDialog}
          onOpenChange={(open) => {
            setShowResolveDialog(open);
            if (!open) setBookingsToResolve([]);
          }}
          bookings={bookingsToResolve as unknown as import('@/hooks/useUnallocatedBookings').UnallocatedBooking[]}
          resources={resources}
          existingBookings={bookings}
          projects={projectDetails as unknown as import('@/types').Project[]}
          onConfirm={handleResolveConfirm}
        />
      )}
    </div>
  );
}
